/**
 * How setup gives a money record its money rule (steps 2.4, 2.7, 2.8 and
 * 2.10 of docs/plans/ledger.md): a posting pattern of the closed set (L4) and
 * its roles mapped to the record's own fields, worked out by code from the
 * record's fields and one answer, never written as lines and never by a
 * model.
 *
 *   the question     "Is this money in, money out, money owed to you, or
 *                    money you owe?" (2.4): one answer for each money
 *                    record, and the answer picks the pattern (`patternOf`).
 *                    `kindGuess` is the answer Decide for me takes.
 *   the roles        the amount is the record's measure; a pick of a list of
 *                    people is the party; a Paid or Received beside the
 *                    amount is what was paid at once (L35); a payment mode is
 *                    an account for each of its choices (L34, L45); a choice
 *                    of what it was for is an account for each of its
 *                    choices (L16); an incentive or a commission worked out
 *                    on the record is its commission (L35).
 *   a mode           Cash is Cash, UPI and a card the bank, Credit and Due
 *                    the receivable (or the payable on money out), a named
 *                    bank account a row of its own, an overdraft a row you
 *                    owe; a name that says none of it is a row whose type
 *                    setup asks for (2.7, `modeAccountOf`).
 *   set apart        a kind of row the file keeps out of profit (Office
 *                    Jama, W24) moves money between two places: a
 *                    `transfer` (L36), never income or an expense.
 *
 * A rule names accounts by system key (`{ systemKey }`, the wave contract's
 * keys) or by a row of the Accounts list (`{ row }`, the word its name
 * holds), and `accountRowsOf` says which rows the Accounts list must hold for
 * it, each with its type (null: setup asks). The rule's shape is the one the
 * draft and the spec carry (`posting` on a record, step 2.3).
 *
 * Pure: the phone bundles it.
 */
import type { SetupDraftField, SetupDraftFormulaNode } from '../compose-author';
import { PATTERN_DIRECTION, PATTERN_ROLES, isPostingPattern, type PostingPattern } from '../ledger/patterns';
import type { SpecPostingRule, SpecRoleSource } from '../spec/posting';
import type { StudioDraft, StudioRecord } from './draft';
import type { StudioOp } from './ops';

/* -------------------------------------------------------------- the shape */

/** Where one role of a money rule reads its value, by the record's field keys: the spec's `SpecRoleSource`. */
export type MoneyRoleSource = SpecRoleSource;

/** A record's money rule, by keys: what `posting` on a draft record holds, the spec's `SpecPostingRule`. */
export type MoneyRule = SpecPostingRule;

/** The five account types (L15). */
export type MoneyAccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';

/** A row the Accounts list must hold for a rule: its name, its type (null when the name does not say it: setup asks, 2.7). */
export type AccountRowNeed = { readonly name: string; readonly type: MoneyAccountType | null };

/** A rule and the rows it names. */
export type MoneyPlan = { readonly rule: MoneyRule; readonly rows: readonly AccountRowNeed[] };

/* ----------------------------------------------------------- the question */

/** The four answers to "Is this money in, money out, money owed to you, or money you owe?" (2.4). */
export const MONEY_KINDS = ['in', 'out', 'owed_to_you', 'you_owe'] as const;
export type MoneyKind = (typeof MONEY_KINDS)[number];

/* ------------------------------------------------------------- the words */

// Whole words only, as the gap rules read them: `mode` must not find "Model".
const PEOPLE = /\b(customers?|clients?|parties|party|buyers?|members?|patients?|farmers?|tenants?|students?|debtors?|creditors?|guests?|people|grahak|suppliers?|vendors?|distributors?|dealers?|wholesalers?|villagers?|contractors?|bookings?|jobs?|orders?)\b/i;
// A site, a lorry or a project is what money is spent on or earned by, never who owes it (30 Oct: a contractor's Sites, a transporter's Lorries).
const THINGS = /\b(items?|products?|goods|materials?|menu|dish(es)?|medicines?|stocks?|sku|maal|articles?|outlets?|branch(es)?|shops?|stores?|locations?|places?|categor(y|ies)|units?|modes?|machines?|models?|vehicles?|rooms?|treatments?|services?|staff|doctors?|sites?|projects?|lorr(y|ies)|trucks?|tempos?|gaadi|gadi|buses?|cars?|animals?|pashu|dangar|cattle|livestock|cows?|buffalo(es)?|bhains|gaay)\b/i;
/** What was paid at once, in the words a bill says it: Paid now, Advance, Jama, Dibayar and Uang muka (a lodge's DP). */
const PAID = /\b(paid|received|advance|jama|paid now|deposit|dibayar|terbayar|uang muka|down payment|dp)\b/i;
const PAID_ON = /\b(received date|paid on|payment date|date paid|date received)\b/i;
const MODE_LABEL = /\b(mode|paid by|paid via|paid through|payment|payments|method|how paid|paid how|cara bayar|pembayaran)\b/i;
const CATEGORY_LABEL = /\b(categor(y|ies)|expenses?|expense head|heads?|what for|for what|purpose|types?|kharch\w*)\b|^\s*for\s*$/i;
const COMMISSION = /\b(incentive|commission|brokerage|cut)\b/i;
const PAYMENT_NAME = /\b(payments?|receipts?|collections?|received|paid back|repayments?|wapsi|vapsi|wapas|vapas)\b/i;
const DUES_NAME = /\b(udhaar|udhar|dues?|credit|khata|owed|baaki|baki|ledger)\b/i;

/** Words of a mode that is where money is: cash in hand. */
const CASH = /\b(cash|nakad|nagad|galla|gulla|till|tunai)\b/i;
/** Words of a mode that is not paid yet (L45): on credit, due, on the room bill. */
const NOT_PAID = /\b(credit|udhaar|udhar|due|dues|unpaid|later|pay later|baaki|baki|khata|pending|room bill|on account|baad\s*(mein|me|men)|bad\s*mein|nanti)\b/i;
/** Words of a mode that is a loan: an overdraft, a cash credit, a card's credit line. */
const OWED_MODE = /\b(od|o\/d|overdraft|cc|cash credit|loan|credit card)\b/i;
/** Words of a mode that is a bank: a transfer, a card, a cheque. */
const BANK_MODE = /\b(upi|gpay|g pay|google pay|phonepe|phone pe|paytm|bhim|cards?|debit|online|bank|cheque|check|neft|imps|rtgs|net ?banking|transfer|qr|qris|duitnow|atm|a\/c|account|wallet|e-?wallet|gopay|ovo|kartu|swipe|pos)\b/i;
/** Words of a mode that is a delivery app: it holds the money until it pays the business, money you have of its own. */
const APP_MODE = /\b(delivery\s+apps?|food\s+apps?|apps?|grab\s?food|foodpanda|swiggy|zomato|shopee\s?food|uber\s?eats|deliveroo)\b/i;
/** The generic bank words: a mode named only by one of these is the first bank, never a row of its own. */
const GENERIC_BANK = /^(upi|gpay|g pay|google pay|phonepe|phone pe|paytm|bhim|card|cards|debit card|debit|online|bank|bank transfer|transfer bank|kartu|kartu debit|cheque|check|neft|imps|rtgs|net banking|netbanking|transfer|qr|atm|wallet|swipe|pos)$/i;

const fold = (words: string): string => words.trim().replace(/\s+/g, ' ');

/**
 * Whether a mode is named only by generic bank words, alone or joined ("Bank
 * / NEFT", "UPI or GPay", "Bank transfer (NEFT, RTGS)"): the first bank. A
 * cloth trader's Kharid paid by "Bank / NEFT" got a row of its own beside
 * Bank, so the bank's money was split across two accounts (khata I6, 30 Oct).
 */
const genericBank = (name: string): boolean => {
  const words = name.split(/\s*(?:[/,&+()]|\bor\b|\band\b)\s*/iu).filter((one) => one.trim() !== '');
  return words.length > 0 && words.every((one) => GENERIC_BANK.test(one.trim()));
};

/**
 * The account a payment mode's name means (L34, L45, 2.7). `side` is the way
 * the money goes: a mode not paid yet is the receivable on money in and the
 * payable on money out.
 *
 *   { source }           an account by its system key, or a row by its name
 *   { row }              the Accounts row it needs, when it is one of its own,
 *                        with its type, or null when the name does not say
 *                        it (setup asks)
 */
export const modeAccountOf = (label: string, side: 'in' | 'out'): { readonly source: MoneyRoleSource; readonly row: AccountRowNeed | null } => {
  const name = fold(label);
  if (OWED_MODE.test(name) && !/\bdebit\b/i.test(name)) return { source: { row: name }, row: { name, type: 'liability' } };
  if (NOT_PAID.test(name)) return { source: { systemKey: side === 'in' ? 'receivable' : 'payable' }, row: null };
  if (CASH.test(name) && !BANK_MODE.test(name)) return { source: { systemKey: 'cash' }, row: null };
  if (BANK_MODE.test(name)) {
    if (genericBank(name)) return { source: { systemKey: 'bank' }, row: null };
    // A bank named ("RSBL A/C", "HDFC Bank"): money you have, a row of its own.
    return { source: { row: name }, row: { name, type: 'asset' } };
  }
  // A delivery app pays the business later: what it holds is money the business has, apart from Cash (M1).
  if (APP_MODE.test(name)) return { source: { row: name }, row: { name, type: 'asset' } };
  return { source: { row: name }, row: { name, type: null } };
};

/**
 * A rule's maps of a choice to accounts once the choice's options changed (S12 of round 1, 30 Oct 2026: "add Card" on a gym's
 * Day passes and "add Paytm" on a dairy's sales were refused as "That field is not on Day passes yet": the rule mapped no "card",
 * so the fields stage's check and its repair refused the record's fields). An option taken off leaves the map. A new one is the
 * account its name means (`modeAccountOf`) when the map already sends a mode to Cash or the bank and that account is one of
 * those, or a row the Accounts list holds (`rows`, its names lower case); any other stays unmapped, and the rule check says so.
 */
export const ruleFollowsOptions = (rule: MoneyRule, field: SetupDraftField, side: 'in' | 'out', rows: ReadonlySet<string>): MoneyRule => {
  if (rule.roles === undefined) return rule;
  const options = field.options ?? [];
  let changed = false;
  const roles = Object.fromEntries(
    Object.entries(rule.roles).map(([role, source]) => {
      if (!('fieldKey' in source) || source.fieldKey !== field.key || source.options === undefined) return [role, source];
      const held = source.options;
      const modes = Object.values(held).some((one) => 'systemKey' in one);
      const next: Record<string, SpecRoleSource> = {};
      for (const option of options) {
        const had = held[option.key];
        if (had !== undefined) {
          next[option.key] = had;
          continue;
        }
        const guess = modeAccountOf(option.label, side).source;
        if (modes && 'systemKey' in guess) next[option.key] = guess;
        else if ('row' in guess && rows.has(guess.row.toLowerCase())) next[option.key] = guess;
      }
      if (JSON.stringify(next) === JSON.stringify(held)) return [role, source];
      changed = true;
      return [role, { ...source, options: next }];
    }),
  );
  return changed ? { ...rule, roles } : rule;
};

/* ---------------------------------------------------------- the fields */

const isMoney = (field: SetupDraftField): boolean => field.type === 'money' || (field.type === 'formula' && field.formula?.result === 'money');

/** Whether a record is the Accounts list (L15): the money kit's list, by its `accounts` mark, never by its name (a business may keep a list it calls Accounts). */
export const isAccountsList = (record: StudioRecord): boolean => record.kind === 'directory' && record.accounts !== undefined;

/** The Accounts list of a draft, when it holds one. */
export const accountsListOf = (draft: Pick<StudioDraft, 'records'>): StudioRecord | null => draft.records.find(isAccountsList) ?? null;

/** The record's posting rule, when it carries one (step 2.3). */
export const postingOf = (record: StudioRecord): MoneyRule | null => ((record as { posting?: MoneyRule }).posting ?? null);

/** The money an entry moves: its measure when that is money, else its first money field. */
export const amountFieldOf = (record: StudioRecord): SetupDraftField | undefined => {
  const measure = record.fields.find((field) => field.key === record.measureFieldKey);
  return measure !== undefined && isMoney(measure) ? measure : record.fields.find(isMoney);
};

/**
 * A money record (L2): an entry record with money on it that counts money in
 * or out. A list never moves money (W11), and a record that counts a
 * quantity (stock received, milk collected) moves stock until Phase 9 gives
 * it quantity lines, whatever its rate.
 */
export const isMoneyRecord = (record: StudioRecord): boolean =>
  record.kind === 'record' && (record.direction === 'in' || record.direction === 'out' || record.direction === 'both') && amountFieldOf(record) !== undefined;

const listWords = (draft: Pick<StudioDraft, 'records'>, field: SetupDraftField): string =>
  `${field.label} ${field.key.replace(/_/g, ' ')} ${draft.records.find((one) => one.key === field.targetRecordKey)?.name ?? ''}`;

/** Who pays the business: on money out they are what a cost was for (a job's customer), never who is owed. */
const PAYERS = /\b(customers?|clients?|patients?|students?|members?|guests?|buyers?|grahak|tenants?|debtors?)\b/i;
/** Who the business pays: on money in they are never who owes. */
const PAYEES = /\b(suppliers?|vendors?|distributors?|dealers?|wholesalers?|creditors?|contractors?|farmers?|villagers?)\b/i;

/**
 * The pick of who the money is with: a list of people, never of what is
 * sold, a place, or the Accounts list. On money out a customer is not who is
 * owed, and on money in a supplier is not who owes.
 */
export const partyFieldOf = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord, side?: 'in' | 'out'): SetupDraftField | undefined => {
  const accounts = accountsListOf(draft)?.key;
  const other = side === 'out' ? PAYERS : side === 'in' ? PAYEES : null;
  const picks = record.fields.filter(
    (field) => field.type === 'reference' && field.targetRecordKey !== undefined && field.targetRecordKey !== accounts && (other === null || !other.test(listWords(draft, field))),
  );
  return picks.find((field) => PEOPLE.test(listWords(draft, field))) ?? picks.find((field) => !THINGS.test(listWords(draft, field)) && draft.records.find((one) => one.key === field.targetRecordKey)?.kind === 'directory');
};

/** Whether a choice is a payment mode: a mode label, or options that are mostly modes. */
const isModeChoice = (field: SetupDraftField): boolean => {
  if (field.type !== 'select') return false;
  const options = field.options ?? [];
  if (options.length < 2 || options.some((option) => option.sign !== undefined)) return false;
  const modes = options.filter((option) => CASH.test(option.label) || BANK_MODE.test(option.label) || APP_MODE.test(option.label) || NOT_PAID.test(option.label) || OWED_MODE.test(option.label));
  // Its label, or its key ("payment_mode" labelled How paid).
  const named = MODE_LABEL.test(field.label) || MODE_LABEL.test(field.key.replace(/_/g, ' '));
  return modes.length >= Math.ceil(options.length / 2) && (named || modes.length === options.length);
};

/** The field that says where the money went or came from: a pick of the Accounts list, or a mode choice. */
export const modeFieldOf = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): SetupDraftField | undefined => {
  const accounts = accountsListOf(draft)?.key;
  return (
    record.fields.find((field) => field.type === 'reference' && accounts !== undefined && field.targetRecordKey === accounts && MODE_LABEL.test(field.label)) ??
    // A choice that names a place money is in (Cash, UPI) before one that only says owed or paid ("On room bill", "Paid on
    // the spot"), which is what an entry was charged to, never where its money went (Meera Residency, 30 Oct).
    record.fields.find((field) => isModeChoice(field) && namesPlace(field)) ??
    record.fields.find(isModeChoice)
  );
};

/** Whether a choice holds an option that is a place money is in: Cash, or a bank's way to pay. */
const namesPlace = (field: SetupDraftField): boolean => (field.options ?? []).some((option) => CASH.test(option.label) || BANK_MODE.test(option.label));

/**
 * A field whose label says where the money went or came from (Paid from
 * account, Paid by, Received in, Mode): never what the money was for, though
 * it picks a row of the Accounts list (G19: an expense counted under its
 * optional "Paid from account" refused every entry that left it empty).
 */
const PLACE_LABEL = /\b(paid\s+(from|by|via|through|into|in|with)|received\s+(in|into|by|via|through)|deposited\s+(in|into|to)|from\s+account|mode|method)\b/i;

/** Whether a field's label says where the money is, not what it was for. */
export const isPlaceLabel = (label: string): boolean => PLACE_LABEL.test(label) || MODE_LABEL.test(label);

/**
 * The field that says what the money was for, never one named for where the
 * money is (G19). In order: a required pick of the Accounts list; a choice of
 * heads (named Category, Head, Type, For, or whose options the Accounts list
 * holds as income or expense rows); an optional pick of the Accounts list only
 * when there is no such choice (the trader's Expenses kept Category, required,
 * beside an optional "Account" pick, and every rent with Account left empty
 * was refused).
 */
export const categoryFieldOf = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): SetupDraftField | undefined => {
  const list = accountsListOf(draft);
  const accounts = list?.key;
  const mode = modeFieldOf(draft, record);
  const picks = record.fields.filter(
    (field) => field.type === 'reference' && accounts !== undefined && field.targetRecordKey === accounts && field.key !== mode?.key && !isPlaceLabel(field.label),
  );
  const heads = list === null ? new Set<string>() : headRowNames(list);
  const choices = record.fields.filter(
    (field) =>
      field.type === 'select' &&
      field.key !== mode?.key &&
      field.key !== record.signFieldKey &&
      !isPlaceLabel(field.label) &&
      (field.options ?? []).length >= 2 &&
      !isModeChoice(field) &&
      (CATEGORY_LABEL.test(field.label) || (field.options ?? []).every((option) => heads.has(fold(option.label).toLowerCase()))),
  );
  return picks.find((field) => field.required === true) ?? choices.find((field) => CATEGORY_LABEL.test(field.label)) ?? choices[0] ?? picks[0];
};

/** The Accounts rows of income or expense type, lower case: the heads a choice's options may name. */
const headRowNames = (list: StudioRecord): Set<string> => {
  const typeKey = list.accounts?.typeFieldKey;
  return new Set(
    (list.seedRows ?? []).flatMap((row) => {
      const type = typeKey === undefined ? null : row.values.find((one) => one.fieldKey === typeKey)?.value;
      if (type !== 'income' && type !== 'expense') return [];
      return accountRowName(list, row)?.toLowerCase() ?? [];
    }),
  );
};

/** What was paid at once: a typed money field named Paid or Received beside the amount (L35). */
export const paidFieldOf = (record: StudioRecord): SetupDraftField | undefined => {
  const amount = amountFieldOf(record);
  return record.fields.find((field) => field.type === 'money' && field.key !== amount?.key && PAID.test(field.label));
};

/** The day a payment was made, when the record keeps one beside the entry's date (L48). */
const paidOnFieldOf = (record: StudioRecord): SetupDraftField | undefined =>
  record.fields.find((field) => field.type === 'date' && field.key !== record.dateFieldKey && PAID_ON.test(field.label));

/** An incentive or a commission worked out on the record (L35): its money is an expense the business owes. */
export const commissionFieldOf = (record: StudioRecord): SetupDraftField | undefined => {
  const amount = amountFieldOf(record);
  return record.fields.find((field) => field.key !== amount?.key && isMoney(field) && COMMISSION.test(field.label));
};

/* ---------------------------------------------------------------- the tax */

/** A rate of tax: GST %, GST 5%, VAT rate, Tax %. */
const TAX_WORD = /\b(gst|vat|tax|igst|cgst|sgst)\b/i;
/** The taxable value of a line or a bill. */
const TAXABLE = /\b(taxable|sub\s*total|subtotal|before (gst|tax|vat)|without (gst|tax|vat)|excl\w*)\b/i;
const HSN = /\b(hsn|sac)\b/i;
/** A total with the tax in it: never the tax itself. */
const WITH_TAX = /\b(?:bill|grand|net|invoice)\s+total\b|\bincl\w*|\bwith\s+(?:gst|tax|vat)\b/i;

const isNumber = (field: SetupDraftField): boolean => field.type === 'number' || (field.type === 'formula' && field.formula?.result === 'number');

/** A rate field: a number named for the tax, never its amount. */
const rateFieldOf = (fields: readonly SetupDraftField[]): SetupDraftField | undefined => fields.find((field) => isNumber(field) && TAX_WORD.test(field.label));

/** Whether a formula is a quantity times a rate: a line's value before tax. */
const isProduct = (field: SetupDraftField): boolean => {
  const expr = (field.formula as { expr?: { kind?: string; op?: string } } | undefined)?.expr;
  return expr?.kind === 'calc' && expr.op === 'multiply';
};

/** A line's value before tax: a field named Taxable, else a money formula of quantity times rate, never one named for the tax. */
const taxableFieldOf = (fields: readonly SetupDraftField[]): SetupDraftField | undefined =>
  fields.find((field) => isMoney(field) && TAXABLE.test(field.label)) ?? fields.find((field) => isMoney(field) && isProduct(field) && !TAX_WORD.test(field.label));

/**
 * The tax roles of a sale or a purchase whose record works out tax (L43, G18):
 *
 *   on its item lines  a lines field whose rows hold a rate of tax (GST %)
 *                      and a value before tax (Taxable value, or Amount as
 *                      quantity times rate): `lines` reads each row's value,
 *                      rate and HSN, so the income is the taxable value and
 *                      CGST and SGST (or IGST) post row by row
 *   on the entry       a rate of tax on the record itself: `tax` reads it,
 *                      and an amount that is a total with tax in it is read
 *                      as a price with tax (`tax_basis` inclusive)
 *
 * Empty when the record keeps no rate. Under no tax regime the roles post
 * nothing more: the entry's amount posts as it is.
 */
export const taxRolesOf = (record: StudioRecord, amountKey?: string): Record<string, MoneyRoleSource> => {
  // The tax the bill works out itself (GST (5%), GST amount, GST total), first: it follows the bill's own GST toggle.
  const amount = amountKey ?? amountFieldOf(record)?.key;
  const taxAmount = record.fields.find((field) => isMoney(field) && TAX_WORD.test(field.label) && !TAXABLE.test(field.label) && !WITH_TAX.test(field.label) && field.key !== amount);
  if (taxAmount !== undefined) {
    const taxable = record.fields.find((field) => isMoney(field) && TAXABLE.test(field.label) && field.key !== taxAmount.key);
    const rate = rateFieldOf(record.fields);
    const hsn = record.fields.find((field) => HSN.test(field.label) && field.type !== 'lines');
    return {
      tax_amount: { fieldKey: taxAmount.key },
      ...(taxable === undefined ? {} : { taxable: { fieldKey: taxable.key } }),
      ...(rate === undefined ? {} : { tax: { fieldKey: rate.key } }),
      ...(hsn === undefined ? {} : { hsn: { fieldKey: hsn.key } }),
    };
  }
  for (const field of record.fields) {
    if (field.type !== 'lines') continue;
    const row = (field.lines?.fields ?? []) as SetupDraftField[];
    const rate = rateFieldOf(row);
    if (rate === undefined) continue;
    const taxable = taxableFieldOf(row);
    const qty = row.find((one) => one.type === 'quantity' || (one.type === 'number' && /\b(qty|quantity|nos?|bags?|count)\b/i.test(one.label)));
    const price = row.find((one) => one.type === 'money' && /\b(rate|price)\b/i.test(one.label) && one.key !== taxable?.key);
    const hsn = row.find((one) => HSN.test(one.label) && one.type !== 'lines');
    if (taxable === undefined && (qty === undefined || price === undefined)) continue;
    const each: Record<string, MoneyRoleSource> = {
      ...(taxable === undefined ? { qty: { fieldKey: qty!.key }, rate: { fieldKey: price!.key } } : { amount: { fieldKey: taxable.key } }),
      tax: { fieldKey: rate.key },
      ...(hsn === undefined ? {} : { hsn: { fieldKey: hsn.key } }),
    };
    return { lines: { fieldKey: field.key, each } };
  }
  const rate = rateFieldOf(record.fields);
  if (rate === undefined) return {};
  const amountField = record.fields.find((field) => field.key === amount);
  const withTax = amountField !== undefined && /\b(total|incl\w*|with (gst|tax|vat))\b/i.test(amountField.label) && !TAXABLE.test(amountField.label);
  const hsn = record.fields.find((field) => HSN.test(field.label) && field.type !== 'lines');
  return { tax: { fieldKey: rate.key }, ...(withTax ? { tax_basis: { const: 'inclusive' } } : {}), ...(hsn === undefined ? {} : { hsn: { fieldKey: hsn.key } }) };
};

/* --------------------------------------------------------------- the rule */

/**
 * The side a dues record is on by who it picks, never by its word: udhaar,
 * credit and khata name both credit given and credit taken. A pick of
 * customers, members or patients is credit given (money owed to you), a
 * pick of suppliers credit taken (money you owe); null when the record is
 * no dues record or picks neither. A kirana's Udhaar of Customers was drawn
 * money out and counted as a purchase (kirana, round 2).
 */
export const duesKindOf = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): 'owed_to_you' | 'you_owe' | null => {
  const words = `${record.name} ${record.key.replace(/_/g, ' ')}`;
  if (!DUES_NAME.test(words) || PAYMENT_NAME.test(words)) return null;
  const accounts = accountsListOf(draft)?.key;
  const picks = record.fields.filter((field) => field.type === 'reference' && field.targetRecordKey !== undefined && field.targetRecordKey !== accounts);
  const payers = picks.some((field) => PAYERS.test(listWords(draft, field)));
  const payees = picks.some((field) => PAYEES.test(listWords(draft, field)));
  return payers === payees ? null : payers ? 'owed_to_you' : 'you_owe';
};

/**
 * The answer Decide for me takes: a record's direction says it, and a dues
 * record says money owed, on the side of who it picks when the draft is
 * given (`duesKindOf`).
 */
export const kindGuess = (record: StudioRecord, draft?: Pick<StudioDraft, 'records'>): MoneyKind => {
  const owed = draft === undefined ? null : duesKindOf(draft, record);
  if (owed !== null) return owed;
  const words = `${record.name} ${record.key.replace(/_/g, ' ')}`;
  if (record.direction === 'out') return DUES_NAME.test(words) && !PAYMENT_NAME.test(words) ? 'you_owe' : 'out';
  return DUES_NAME.test(words) && !PAYMENT_NAME.test(words) ? 'owed_to_you' : 'in';
};

/** Rows a plan names, once each by name. */
const rowsOnce = (rows: readonly (AccountRowNeed | null)[]): AccountRowNeed[] => {
  const out = new Map<string, AccountRowNeed>();
  for (const row of rows) if (row !== null && !out.has(row.name.toLowerCase())) out.set(row.name.toLowerCase(), row);
  return [...out.values()];
};

/** Where the money went or came from: the mode's choices each an account, the Accounts pick, else Cash. */
const placeOf = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord, side: 'in' | 'out'): { source: MoneyRoleSource; rows: AccountRowNeed[]; field?: SetupDraftField } => {
  const mode = modeFieldOf(draft, record);
  if (mode === undefined) return { source: { systemKey: 'cash' }, rows: [] };
  if (mode.type === 'reference') return { source: { fieldKey: mode.key }, rows: [], field: mode };
  const mapped = (mode.options ?? []).map((option) => ({ key: option.key, ...modeAccountOf(option.label, side) }));
  return {
    source: { fieldKey: mode.key, options: Object.fromEntries(mapped.map((one) => [one.key, one.source])) },
    rows: rowsOnce(mapped.map((one) => one.row)),
    field: mode,
  };
};

/** What the money was for: each choice an income or expense row of its own name, the Accounts pick, else `fallback`. */
const forOf = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord, type: 'income' | 'expense', fallback: MoneyRoleSource, fallbackRow: AccountRowNeed | null): { source: MoneyRoleSource; rows: AccountRowNeed[] } => {
  const category = categoryFieldOf(draft, record);
  if (category === undefined) return { source: fallback, rows: rowsOnce([fallbackRow]) };
  if (category.type === 'reference') return { source: { fieldKey: category.key }, rows: [] };
  const options = category.options ?? [];
  return {
    source: { fieldKey: category.key, options: Object.fromEntries(options.map((option) => [option.key, { row: fold(option.label) }])) },
    rows: rowsOnce(options.map((option) => ({ name: fold(option.label), type }))),
  };
};

/** The record's own name as an account row: "Lab bills" is an expense the clinic keeps. */
const ownRow = (record: StudioRecord, type: 'income' | 'expense'): AccountRowNeed => ({ name: fold(record.name), type });

const roleOf = (field: SetupDraftField | undefined): MoneyRoleSource | undefined => (field === undefined ? undefined : { fieldKey: field.key });

const withRoles = (roles: Record<string, MoneyRoleSource | undefined>): Record<string, MoneyRoleSource> =>
  Object.fromEntries(Object.entries(roles).filter((entry): entry is [string, MoneyRoleSource] => entry[1] !== undefined));

/** A sale's paid part and its commission (L35), when the record keeps them. */
const saleExtras = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): { roles: Record<string, MoneyRoleSource | undefined>; rows: AccountRowNeed[] } => {
  const paid = paidFieldOf(record);
  const place = placeOf(draft, record, 'in');
  const commission = commissionFieldOf(record);
  const commissionName = commission === undefined ? null : fold(commission.label);
  return {
    roles: {
      paid_now: roleOf(paid),
      paid_on: paid === undefined ? undefined : roleOf(paidOnFieldOf(record)),
      // Paid at once into the mode's account; with no mode and no paid field, the whole sale lands where the mode says.
      paid_to: paid !== undefined || place.field !== undefined ? place.source : undefined,
      commission: roleOf(commission),
      commission_account: commissionName === null ? undefined : { row: commissionName },
      commission_payable: commissionName === null ? undefined : { row: `${commissionName} payable` },
    },
    rows: [
      ...(paid !== undefined || place.field !== undefined ? place.rows : []),
      ...(commissionName === null ? [] : [{ name: commissionName, type: 'expense' as const }, { name: `${commissionName} payable`, type: 'liability' as const }]),
    ],
  };
};

/** A purchase's paid part (L47). */
const purchaseExtras = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): { roles: Record<string, MoneyRoleSource | undefined>; rows: AccountRowNeed[] } => {
  const paid = paidFieldOf(record);
  const place = placeOf(draft, record, 'out');
  return {
    roles: {
      paid_now: roleOf(paid),
      paid_on: paid === undefined ? undefined : roleOf(paidOnFieldOf(record)),
      paid_to: paid !== undefined || place.field !== undefined ? place.source : undefined,
    },
    rows: paid !== undefined || place.field !== undefined ? place.rows : [],
  };
};

/** Whether the record's money is a quantity times a rate: goods that change hands, never a payment of what is owed. */
export const goodsAtRate = (record: StudioRecord): boolean => {
  const expr = amountFieldOf(record)?.formula?.expr;
  if (expr === undefined || expr.kind !== 'calc' || expr.op !== 'multiply') return false;
  return [expr.left, expr.right].some((side) => side.kind === 'field' && record.fields.find((field) => field.key === side.fieldKey)?.type === 'quantity');
};

/** Whether another money-out record buys on credit: its mode offers a choice not paid yet, or its rule puts money in what you owe. */
const boughtOnCredit = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): boolean =>
  draft.records.some((other) => {
    if (other.key === record.key || other.direction !== 'out') return false;
    const rule = postingOf(other);
    return rule === null ? offersCredit(draft, other) : ruleNamesKey(rule, 'payable');
  });

/** Whether a role source names this system key, in its options and its rows too. */
const namesKey = (source: MoneyRoleSource, key: string): boolean =>
  'systemKey' in source ? source.systemKey === key : 'fieldKey' in source && [...Object.values(source.options ?? {}), ...Object.values(source.each ?? {})].some((one) => namesKey(one, key));

/** Whether a rule names this system key in any role, its choices' too. */
const ruleNamesKey = (rule: MoneyRule, key: string): boolean =>
  [rule.roles ?? {}, ...Object.values(rule.byChoice?.options ?? {}).map((one) => one.roles ?? {})].some((roles) => Object.values(roles).some((one) => namesKey(one, key)));

/** Whether the record's mode offers a choice not paid yet: a credit sale, a bill to pay later. */
const offersCredit = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): boolean =>
  (modeFieldOf(draft, record)?.options ?? []).some((option) => NOT_PAID.test(option.label) && !OWED_MODE.test(option.label));

/**
 * The pattern an answer picks for a record (2.4):
 *
 *   money in        a payment from a party is a `receipt`; a sale to a party,
 *                   or one that may be on credit or paid in part, a `sale`;
 *                   anything else `money_in` (income when cash comes in, L24)
 *   money out       a payment to a party is a `payment`; a bill from a party,
 *                   or one on credit or paid in part, a `purchase`; anything
 *                   else `money_out`
 *   owed to you     a `sale`: the receivable goes up (L24)
 *   you owe         a `purchase`: the payable goes up
 */
export const patternOf = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord, kind: MoneyKind): PostingPattern => {
  const party = partyFieldOf(draft, record, kind === 'in' || kind === 'owed_to_you' ? 'in' : 'out') !== undefined;
  // Goods counted out at a rate are bought or sold, whatever the record is called: a dairy's milk "collection" (31 Oct).
  const paymentName = PAYMENT_NAME.test(`${record.name} ${record.entryNoun}`) && !goodsAtRate(record);
  const owes = paidFieldOf(record) !== undefined || offersCredit(draft, record);
  switch (kind) {
    case 'in':
      if (paymentName && party) return 'receipt';
      return party || owes ? 'sale' : 'money_in';
    case 'out':
      // A payment that names no one still pays what was bought on credit (a transporter's Bunk payments), never a new cost.
      if (paymentName && (party || boughtOnCredit(draft, record))) return 'payment';
      return party || owes ? 'purchase' : 'money_out';
    case 'owed_to_you':
      return 'sale';
    case 'you_owe':
      return 'purchase';
  }
};

/* ------------------------------------------------ what other records charge */

/**
 * The records whose own rule posts a sale with its party read through a pick
 * of `key` (G31, the hotel of 30 Oct): a service order charged to a booking.
 * Each with the pick it reads.
 */
export const chargedThrough = (draft: Pick<StudioDraft, 'records'>, key: string): Array<{ readonly record: StudioRecord; readonly via: string }> =>
  draft.records.flatMap((other) => {
    if (other.key === key) return [];
    const rule = postingOf(other);
    if (rule === null) return [];
    const party = rule.roles?.party ?? Object.values(rule.byChoice?.options ?? {}).map((one) => one.roles?.party).find((one) => one !== undefined);
    if (party === undefined || !('fieldKey' in party)) return [];
    const pick = other.fields.find((field) => field.key === party.fieldKey);
    return pick?.type === 'reference' && pick.targetRecordKey === key ? [{ record: other, via: pick.key }] : [];
  });

/** The record's own pick a record charged through could also name: any pick of it on another record, ruled or not yet. */
const pickedBy = (draft: Pick<StudioDraft, 'records'>, key: string): boolean =>
  draft.records.some((other) => other.key !== key && other.kind === 'record' && other.fields.some((field) => field.type === 'reference' && field.targetRecordKey === key));

type FormulaNode = { readonly kind?: string; readonly op?: string; readonly left?: FormulaNode; readonly right?: FormulaNode; readonly fieldKey?: string };

/** The terms of a sum: `a + b + c` as its three, anything else as itself. */
const termsOf = (node: FormulaNode): FormulaNode[] => (node.kind === 'calc' && node.op === 'add' && node.left !== undefined && node.right !== undefined ? [...termsOf(node.left), ...termsOf(node.right)] : [node]);

/**
 * The field a record's money is, without a total over the entries other
 * records charge to it themselves (L33, one source): the hotel's Total was
 * Room rent + Room bill, the Room bill a total over the service orders, and
 * each order posts its own sale to the booking, so a booking counted its food
 * twice, at its own tax. The amount reads what is left when that one field is
 * left: Room rent. Null when there is nothing to take out, or when what is left
 * is no one field.
 */
export const ownAmountOf = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord, amountKey: string): SetupDraftField | null => {
  const amount = record.fields.find((field) => field.key === amountKey);
  const expr = (amount?.formula as { expr?: FormulaNode } | undefined)?.expr;
  if (amount === undefined || amount.type !== 'formula' || expr === undefined) return null;
  const charged = chargedThrough(draft, record.key);
  if (charged.length === 0) return null;
  const isCharged = (key: string | undefined): boolean => {
    const field = record.fields.find((one) => one.key === key);
    return field?.type === 'rollup' && field.rollup !== undefined && charged.some((one) => one.record.key === field.rollup!.overRecordKey && one.via === field.rollup!.viaFieldKey);
  };
  const terms = termsOf(expr);
  const kept = terms.filter((term) => !(term.kind === 'field' && isCharged(term.fieldKey)));
  if (kept.length === terms.length || kept.length !== 1 || kept[0]!.kind !== 'field') return null;
  return record.fields.find((field) => field.key === kept[0]!.fieldKey && isMoney(field)) ?? null;
};

/** Words of an option that is paid there and then; and of one put on what someone owes. */
const PAID_NOW = /\b(paid|pay now|at once|now|on the spot|cash|spot)\b/i;

/**
 * The choice that says whether an entry charged to a party is paid there and
 * then or put on what the party owes (the hotel's Charged to: Room bill, Paid
 * at once): a choice that is no mode, with one option of each. Null when the
 * record keeps none, or no mode to say where paid money went.
 */
export const chargedToOf = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): { readonly field: SetupDraftField; readonly owed: string[]; readonly paid: string[] } | null => {
  const mode = modeFieldOf(draft, record);
  if (mode === undefined) return null;
  for (const field of record.fields) {
    if (field.type !== 'select' || field.key === mode.key || field.key === record.signFieldKey || (field.options ?? []).length < 2) continue;
    const owed = (field.options ?? []).filter((option) => NOT_PAID.test(option.label)).map((option) => option.key);
    const paid = (field.options ?? []).filter((option) => !NOT_PAID.test(option.label) && PAID_NOW.test(option.label)).map((option) => option.key);
    if (owed.length === 0 || paid.length === 0 || owed.length + paid.length !== (field.options ?? []).length) continue;
    return { field, owed, paid };
  }
  return null;
};

/** A sale's rule split by its charged-to choice: on what the party owes, with no place; paid now to the mode's account. */
const chargedToRule = (rule: MoneyRule, charged: NonNullable<ReturnType<typeof chargedToOf>>, place: MoneyRoleSource): MoneyRule => {
  const { paid_to: _paidTo, paid_now: _paidNow, paid_on: _paidOn, ...base } = rule.roles ?? {};
  return {
    pattern: 'sale',
    roles: base,
    byChoice: {
      fieldKey: charged.field.key,
      options: Object.fromEntries([
        ...charged.owed.map((key) => [key, { pattern: 'sale' as const }]),
        ...charged.paid.map((key) => [key, { pattern: 'sale' as const, roles: { paid_to: place } }]),
      ]),
    },
  };
};

/** One pattern's rule for a record, and the rows it names; null when the record moves no money. */
const planFor = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord, pattern: PostingPattern): MoneyPlan | null => {
  const measure = amountFieldOf(record);
  if (measure === undefined) return null;
  // Never a total over what other records post themselves (L33): the record's own part.
  const amount = ownAmountOf(draft, record, measure.key) ?? measure;
  const side = pattern === 'purchase' || pattern === 'money_out' || pattern === 'payment' ? 'out' : 'in';
  // A sale others charge to (a booking its orders pick) is its own party (L46), when it picks no party of its own.
  const picked = partyFieldOf(draft, record, side);
  const party: MoneyRoleSource | undefined = picked !== undefined ? roleOf(picked) : pattern === 'sale' && pickedBy(draft, record.key) ? { self: true } : undefined;
  const base = { amount: { fieldKey: amount.key } as MoneyRoleSource, party };
  switch (pattern) {
    case 'money_in': {
      const place = placeOf(draft, record, 'in');
      const what = forOf(draft, record, 'income', { systemKey: 'sales' }, null);
      return { rule: { pattern, roles: withRoles({ ...base, account: what.source, paid_to: place.source }) }, rows: rowsOnce([...what.rows, ...place.rows]) };
    }
    case 'money_out': {
      const place = placeOf(draft, record, 'out');
      const what = forOf(draft, record, 'expense', { row: fold(record.name) }, ownRow(record, 'expense'));
      return { rule: { pattern, roles: withRoles({ ...base, account: what.source, paid_from: place.source }) }, rows: rowsOnce([...what.rows, ...place.rows]) };
    }
    case 'receipt': {
      const place = placeOf(draft, record, 'in');
      return { rule: { pattern, roles: withRoles({ ...base, paid_to: place.source }) }, rows: place.rows };
    }
    case 'payment': {
      const place = placeOf(draft, record, 'out');
      return { rule: { pattern, roles: withRoles({ ...base, paid_from: place.source }) }, rows: place.rows };
    }
    case 'sale': {
      const what = forOf(draft, record, 'income', { systemKey: 'sales' }, null);
      const extras = saleExtras(draft, record);
      const rule: MoneyRule = { pattern, roles: withRoles({ ...base, income: what.source, ...extras.roles, ...taxRolesOf(record, amount.key) }) };
      // Charged to a party, or paid there and then (the hotel's Charged to): a sale on credit, or one paid to the mode's account.
      const charged = base.party !== undefined && 'fieldKey' in base.party ? chargedToOf(draft, record) : null;
      const place = placeOf(draft, record, 'in');
      return { rule: charged === null ? rule : chargedToRule(rule, charged, place.source), rows: rowsOnce([...what.rows, ...extras.rows, ...(charged === null ? [] : place.rows)]) };
    }
    case 'purchase': {
      const what = forOf(draft, record, 'expense', { row: fold(record.name) }, ownRow(record, 'expense'));
      const extras = purchaseExtras(draft, record);
      return { rule: { pattern, roles: withRoles({ ...base, expense: what.source, ...extras.roles, ...taxRolesOf(record, amount.key) }) }, rows: rowsOnce([...what.rows, ...extras.rows]) };
    }
    default:
      return null;
  }
};

/**
 * Whether a field shows on an entry whose sign choice is `option`: a field
 * shown only for another option ("Mill" shown when Paid to mill) is not on
 * it. A show rule that reads anything else keeps the field.
 */
const shownFor = (field: SetupDraftField, signKey: string, option: string): boolean => {
  const expr = (field.showWhen as { expr?: { kind?: string; op?: string; left?: { kind?: string; fieldKey?: string }; right?: { kind?: string; value?: unknown } } } | undefined)?.expr;
  if (expr?.kind !== 'if' || expr.left?.kind !== 'field' || expr.left.fieldKey !== signKey || expr.right?.kind !== 'const') return true;
  if (expr.op === 'eq') return expr.right.value === option;
  if (expr.op === 'neq') return expr.right.value !== option;
  return true;
};

/**
 * A record that goes both ways (L44) picks its pattern for each entry by the
 * choice that signs it: a dues record's Gave is a sale on credit and its Got
 * a receipt (Bill and Paid, a purchase and a payment, on money owed out); a
 * payment record's Received is a receipt and its Paid a payment (a yarn
 * trader's Payment: received from a party, paid to a mill, M3); a cash
 * record's In is money in and its Out money out. Each option reads the
 * fields its entries show: Received names the Party, Paid the Mill.
 */
const bothWays = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): MoneyPlan | null => {
  const sign = record.fields.find((field) => field.key === record.signFieldKey && field.type === 'select');
  if (sign === undefined) return null;
  const words = `${record.name} ${record.key.replace(/_/g, ' ')}`;
  const dues = DUES_NAME.test(words);
  const paying = !dues && PAYMENT_NAME.test(words);
  const owedOut = dues && /\b(suppliers?|vendors?|distributors?|bills?|payable|you owe)\b/i.test(`${record.name} ${record.fields.map((one) => one.label).join(' ')}`);
  const options: Record<string, { pattern: PostingPattern; roles: Record<string, MoneyRoleSource> }> = {};
  const rows: AccountRowNeed[] = [];
  for (const option of sign.options ?? []) {
    if (option.sign === undefined) return null;
    const pattern: PostingPattern = dues
      ? owedOut
        ? option.sign === 1
          ? 'purchase'
          : 'payment'
        : option.sign === 1
          ? 'sale'
          : 'receipt'
      : paying
        ? option.sign === 1
          ? 'receipt'
          : 'payment'
        : option.sign === 1
          ? 'money_in'
          : 'money_out';
    const fields = record.fields.filter((field) => shownFor(field, sign.key, option.key));
    const plan = planFor(draft, { ...record, fields, signFieldKey: undefined }, pattern);
    if (plan === null) return null;
    options[option.key] = { pattern, roles: { ...(plan.rule.roles ?? {}) } };
    rows.push(...plan.rows);
  }
  const first = Object.values(options)[0];
  if (first === undefined) return null;
  return { rule: { pattern: first.pattern, byChoice: { fieldKey: sign.key, options } }, rows: rowsOnce(rows) };
};

/**
 * The rule an answer gives a money record, and the Accounts rows it names;
 * null for a record that moves no money. A record signed by a choice goes
 * both ways whatever the answer (L44).
 */
export const moneyPlanOf = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord, kind: MoneyKind = kindGuess(record, draft)): MoneyPlan | null => {
  if (!isMoneyRecord(record)) return null;
  if (record.direction === 'both') return bothWays(draft, record);
  return planFor(draft, record, patternOf(draft, record, kind));
};

/**
 * A move between two places of the business's own money (L36, W24): cash an
 * operator holds handed in to the office, cash put in the bank. From `from`
 * into where the record's mode says (Cash into `cashTo`), never income or an
 * expense, so profit leaves it out with no list of exclusions.
 */
export const transferPlanOf = (
  draft: Pick<StudioDraft, 'records'>,
  record: StudioRecord,
  places: { readonly from: MoneyRoleSource; readonly cashTo: AccountRowNeed },
): MoneyPlan | null => {
  const amount = amountFieldOf(record);
  if (amount === undefined) return null;
  const mode = modeFieldOf(draft, record);
  const rows: AccountRowNeed[] = [];
  let to: MoneyRoleSource = { row: places.cashTo.name };
  if (mode?.type === 'select') {
    const options: Record<string, MoneyRoleSource> = {};
    for (const option of mode.options ?? []) {
      const account = modeAccountOf(option.label, 'out');
      // Cash handed in is the office's cash; a deposit by UPI or at an ATM lands in the bank.
      const cash = 'systemKey' in account.source && account.source.systemKey === 'cash';
      options[option.key] = cash ? { row: places.cashTo.name } : account.source;
      if (cash) rows.push(places.cashTo);
      else if (account.row !== null) rows.push(account.row);
    }
    to = { fieldKey: mode.key, options };
  } else if (mode?.type === 'reference') {
    to = { fieldKey: mode.key };
  } else {
    rows.push(places.cashTo);
  }
  return { rule: { pattern: 'transfer', roles: { amount: { fieldKey: amount.key }, from: places.from, to } }, rows: rowsOnce(rows) };
};

/** The Accounts rows a rule names that the list does not hold yet, by the word its name holds (lower case). */
export const missingRows = (plan: MoneyPlan, held: ReadonlySet<string>): AccountRowNeed[] => plan.rows.filter((row) => !held.has(row.name.toLowerCase()));

/* -------------------------------------------------------------- the ops */

/** The forms the money kit makes (L3): each carries its own rule, and setup never asks about them. */
const MONEY_KIT_FORMS = /^(money in|money out|adjustment|opening balance)$/i;

/** Whether a record is one of the money kit's four forms. */
export const isMoneyKitForm = (record: Pick<StudioRecord, 'name'>): boolean => MONEY_KIT_FORMS.test(record.name.trim());

type SeedRow = NonNullable<StudioRecord['seedRows']>[number];

/** The name a seeded row of the Accounts list holds, or null. */
export const accountRowName = (list: StudioRecord, row: SeedRow): string | null => {
  const held = row.values.find((one) => one.fieldKey === list.headlineFieldKey)?.value;
  return held === undefined ? null : String(held).trim();
};

/** The words each seeded row of the Accounts list holds in its name, lower case. */
export const accountRowNames = (list: StudioRecord): Set<string> =>
  new Set((list.seedRows ?? []).flatMap((row) => accountRowName(list, row)?.toLowerCase() ?? []));

/** A seed row of the Accounts list: its name, and its type when the list names its Type field. A type not known yet starts as money you have, and setup asks (2.7). */
export const accountSeedRow = (list: StudioRecord, row: AccountRowNeed, systemKey?: string): SeedRow => {
  const typeKey = list.accounts?.typeFieldKey;
  return {
    values: [{ fieldKey: list.headlineFieldKey, value: row.name }, ...(typeKey === undefined ? [] : [{ fieldKey: typeKey, value: row.type ?? 'asset' }])],
    ...(systemKey === undefined ? {} : { account: { systemKey } }),
  };
};

/**
 * The ops that give a record its rule: the Accounts rows the rule names that
 * the list does not hold yet, then the rule. Null when the draft holds no
 * Accounts list: a rule names its accounts there, so the money kit comes
 * first (L3).
 */
export const moneyPlanOps = (draft: Pick<StudioDraft, 'records'>, recordKey: string, plan: MoneyPlan): StudioOp[] | null => {
  const list = accountsListOf(draft);
  if (list === null) return null;
  const rows = missingRows(plan, accountRowNames(list));
  return [
    ...(rows.length === 0 ? [] : [{ op: 'seed_list' as const, record: list.key, rows: rows.map((row) => accountSeedRow(list, row)), mode: 'append' as const }]),
    { op: 'update_record', record: recordKey, posting: plan.rule },
  ];
};

/** The reason a money question keeps when the person said the record moves no money: no rule is drawn for it again. */
export const MONEY_NONE_REASON = 'moves no money';

/**
 * The money rules a draft's records still lack (L59): each money record that
 * carries no rule gets the rule its own fields say (`moneyPlanOf`, with the
 * answer Decide for me takes), unless `planOf` names a better one (a kind a
 * file sets apart is a transfer). A record the person said moves no money
 * gets none, and neither does a form of the money kit, which has its own.
 * Empty when the draft holds no Accounts list.
 */
export const missingMoneyRuleOps = (
  draft: Pick<StudioDraft, 'records' | 'dismissedGaps'>,
  planOf: (record: StudioRecord, draft: Pick<StudioDraft, 'records'>) => MoneyPlan | null = () => null,
): StudioOp[] => {
  if (accountsListOf(draft) === null) return [];
  const none = new Set(draft.dismissedGaps.filter((one) => one.reason === MONEY_NONE_REASON).map((one) => one.gap));
  const ops: StudioOp[] = [];
  let running: Pick<StudioDraft, 'records'> = draft;
  for (const record of draft.records) {
    // A rule that moves only stock (Phase 9) counts no money yet: its record gets its money rule, and keeps the stock it moves.
    const held = postingOf(record);
    const stockOnly = held !== null && held.pattern === 'quantity' ? held.quantity : undefined;
    if (!isMoneyRecord(record) || (held !== null && stockOnly === undefined) || isMoneyKitForm(record) || none.has(`money_kind:${record.key}`)) continue;
    const plan = planOf(record, running) ?? moneyPlanOf(running, record);
    if (plan === null) continue;
    const planned = moneyPlanOps(running, record.key, plan);
    if (planned === null) continue;
    const made =
      stockOnly === undefined
        ? planned
        : planned.map((op) => (op.op === 'update_record' && op.record === record.key && op.posting !== undefined ? { ...op, posting: { ...op.posting, quantity: stockOnly } } : op));
    ops.push(...made);
    // The rows one rule adds are held for the next, so two records that name Cash with operators add it once.
    const added = made.flatMap((op) => (op.op === 'seed_list' ? op.rows : []));
    const list = accountsListOf(running)!;
    if (added.length > 0) running = { records: running.records.map((one) => (one.key === list.key ? { ...one, seedRows: [...(one.seedRows ?? []), ...added] } : one)) };
  }
  return ops;
};

/* ------------------------------------------------------- a wrong way */

/** The patterns that pay what is owed, and those that make new income or a new cost. */
const SETTLES: ReadonlySet<string> = new Set(['receipt', 'payment']);
const MAKES: ReadonlySet<string> = new Set(['sale', 'money_in', 'purchase', 'money_out']);

/**
 * Whether a payment's rule has something to pay off: what was bought on
 * credit, when it names no one, or another record of the same way that
 * picks the same list (Udhaar beside Udhaar wapsi), or that list's charges.
 */
const somethingToRepay = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord, rule: MoneyRule): boolean => {
  const party = rule.roles?.party;
  if (party === undefined || !('fieldKey' in party)) return true;
  const list = record.fields.find((field) => field.key === party.fieldKey)?.targetRecordKey;
  if (list === undefined) return false;
  if (draft.records.find((one) => one.key === list)?.chargePlan !== undefined) return true;
  return draft.records.some(
    (other) => other.key !== record.key && other.direction === record.direction && other.fields.some((field) => field.type === 'reference' && field.targetRecordKey === list),
  );
};

/**
 * A dues record's sale or purchase (Udhaar, credit given) is all on credit:
 * a mode beside it that offers no choice not paid yet says nothing of where
 * money went, so the rule puts none of it there.
 */
const onCredit = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord, plan: MoneyPlan): MoneyPlan => {
  const words = `${record.name} ${record.key.replace(/_/g, ' ')}`;
  if ((plan.rule.pattern !== 'sale' && plan.rule.pattern !== 'purchase') || !DUES_NAME.test(words) || PAYMENT_NAME.test(words)) return plan;
  if (paidFieldOf(record) !== undefined || offersCredit(draft, record) || plan.rule.roles?.paid_to === undefined) return plan;
  const { paid_to: _nowhere, ...roles } = plan.rule.roles;
  return { rule: { ...plan.rule, roles }, rows: plan.rows };
};

/**
 * The ops that give a record the rule its shape and words say when the rule
 * it holds goes the wrong way (30 Oct money runs): money in counted as a cost
 * (a kirana's Udhaar, credit given, posted as a purchase), a repayment
 * counted as new income or a new cost (Udhaar wapsi as a sale, a
 * transporter's Bunk payments as an expense beside the diesel bought on
 * credit), or a party that is what the money was spent on, never who owes
 * it (a contractor's Sites, a transporter's Lorries: their Billed and
 * Received mixed hapta and expenses). A rule against its record's side is
 * overturned only when the record's name says dues or a payment. The first
 * two take the rule
 * `moneyPlanOf` draws; a party that is a site or a lorry gives way to the
 * record's pick of people (the dealer), else the rule names no party. A
 * question settled is no guard: a kirana's Udhaar stood settled as a
 * purchase after the owner said twice it was money owed to him. A record
 * that goes both ways under one rule takes the rule each choice says. Empty
 * when every rule goes the way its record does.
 */
export const wrongWayRuleOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  if (accountsListOf(draft) === null) return [];
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const held = postingOf(record);
    if (held === null || !isMoneyRecord(record) || isMoneyKitForm(record)) continue;
    if (held.byChoice !== undefined || !isPostingPattern(held.pattern)) continue;
    // A record that goes both ways under one rule counts every entry one way (a Payment's Paid to mill as a receipt
    // from no one, left in To sort, M3): it takes the rule each choice says.
    if (record.direction === 'both') {
      const drawn = MAKES.has(held.pattern) || SETTLES.has(held.pattern) ? moneyPlanOf(draft, record) : null;
      if (drawn?.rule.byChoice !== undefined) ops.push(...(moneyPlanOps(draft, record.key, drawn) ?? []));
      continue;
    }
    const way = PATTERN_DIRECTION[held.pattern];
    // A dues record's side is who it picks (Udhaar of Customers drawn money out is still credit given), else its direction.
    const owed = duesKindOf(draft, record);
    const said = owed !== null ? (owed === 'owed_to_you' ? 1 : -1) : record.direction === 'in' ? 1 : -1;
    const drawn = moneyPlanOf(draft, record);
    // Only the record's own words overturn a rule against its side (Udhaar on money in is money owed to you): a rule the person chose for Sales stands.
    const words = `${record.name} ${record.key.replace(/_/g, ' ')}`;
    const wrongSide = way !== 0 && way !== said && (DUES_NAME.test(words) || PAYMENT_NAME.test(words));
    const repaid = drawn !== null && SETTLES.has(drawn.rule.pattern) && MAKES.has(held.pattern) && somethingToRepay(draft, record, drawn.rule);
    // Goods counted out at a rate held as a payment: the milk a dairy buys from its gwale counted no cost and owed no one (31 Oct).
    const bought = drawn !== null && SETTLES.has(held.pattern) && MAKES.has(drawn.rule.pattern) && goodsAtRate(record);
    if ((wrongSide || repaid || bought) && drawn !== null && drawn.rule.pattern !== held.pattern) {
      ops.push(...(moneyPlanOps(draft, record.key, onCredit(draft, record, drawn)) ?? []));
      continue;
    }
    // A party that is a site or a lorry: the record's pick of people, else none.
    const party = held.roles?.party;
    if (party === undefined || !('fieldKey' in party)) continue;
    const pick = record.fields.find((field) => field.key === party.fieldKey);
    if (pick === undefined || !THINGS.test(listWords(draft, pick)) || PEOPLE.test(listWords(draft, pick))) continue;
    const people = partyFieldOf(draft, record, record.direction === 'in' ? 'in' : 'out');
    const { party: _thing, ...roles } = held.roles ?? {};
    ops.push({ op: 'update_record', record: record.key, posting: { ...held, roles: people === undefined || people.key === pick.key ? roles : { ...roles, party: { fieldKey: people.key } } } });
  }
  return ops;
};

/* --------------------------------------------------- money a person holds */

/** A record's name that says an advance or a float handed to someone to spend. */
const ADVANCE_NAME = /\b(advances?|imprest|float|peshgi|uchanti)\b/i;
/** Who holds it: one of the business's own people, never a supplier or a customer. */
const HOLDER = /\b(driver|staff|employee|worker|helper|munim|cashier|manager|operator|salesman|conductor|cleaner|khalasi|supervisor|mistri)s?\b/i;
/** A field that says what the holder still has: "Balance with driver", "Cash with staff". */
const WITH_HOLDER = /\b(balance|cash|money)\s+with\b/i;

/**
 * The ops that keep an advance handed to the business's own people as money
 * they hold, never a cost (M3, a transporter's Driver advances, 30 Oct): the
 * cash given to a driver for the road was counted as an expense, so profit
 * fell the day it was handed over, before a rupee was spent. A money-out
 * record whose name says an advance to a driver or staff (or that keeps a
 * "Balance with driver") moves the money from where it was paid into an asset
 * row, "Cash with drivers"; what is spent from it is counted as each expense
 * is entered. A record whose rule moves money between places already stays.
 */
export const advanceHeldOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  if (accountsListOf(draft) === null) return [];
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const held = postingOf(record);
    if (held === null || held.byChoice !== undefined || !isMoneyRecord(record) || record.direction !== 'out') continue;
    if (held.pattern !== 'purchase' && held.pattern !== 'money_out') continue;
    const words = `${record.name} ${record.key.replace(/_/g, ' ')}`;
    const holder = HOLDER.exec(words)?.[1] ?? (record.fields.some((field) => WITH_HOLDER.test(field.label)) ? HOLDER.exec(record.fields.map((field) => field.label).join(' '))?.[1] : undefined);
    if (!ADVANCE_NAME.test(words) || holder === undefined) continue;
    const amount = held.roles?.amount;
    const from = held.roles?.paid_to ?? held.roles?.paid_from ?? placeOf(draft, record, 'out').source;
    if (amount === undefined) continue;
    const word = holder.toLowerCase();
    const row: AccountRowNeed = { name: `Cash with ${word === 'staff' ? word : `${word}s`}`, type: 'asset' };
    ops.push(...(moneyPlanOps(draft, record.key, { rule: { pattern: 'transfer', roles: { amount, from, to: { row: row.name } } }, rows: [row] }) ?? []));
  }
  return ops;
};

/* ------------------------------------------------------ a misread amount */

/** The row names a role source reads, in its options and its rows too. */
const rowsNamedBy = (source: MoneyRoleSource): string[] => {
  if ('row' in source) return [source.row.trim().toLowerCase()];
  if (!('fieldKey' in source)) return [];
  return [...Object.values(source.options ?? {}), ...Object.values(source.each ?? {})].flatMap(rowsNamedBy);
};

/** A money field that is one unit's price, never the whole: Room rate, Price per night, Tariff. */
const UNIT_RATE = /\b(rate|tariff|per\s+(night|day|hour|unit|month|kg|piece))\b/i;

/**
 * The ops that mend a sale's or a purchase's rule whose amount reads what
 * was paid at once, or one unit's rate: a rule written while Paid now was the record's only
 * money, and kept after Total joined it, refuses every credit sale, since a
 * credit sale pays nothing now. The amount becomes the record's own money
 * (its measure), and the roles its fields say (`planFor`) give the party,
 * the paid part and its mode's accounts. An income or expense account the
 * rule named stays, unless the record's own choice says it, and so does any
 * other role it mapped. A rule whose amount reads the record's own money is
 * never touched.
 */
export const misreadAmountOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const list = accountsListOf(draft);
  if (list === null) return [];
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined || (rule.pattern !== 'sale' && rule.pattern !== 'purchase')) continue;
    const source = rule.roles?.amount;
    if (source === undefined || !('fieldKey' in source)) continue;
    const read = record.fields.find((field) => field.key === source.fieldKey);
    const own = amountFieldOf(record);
    // What was paid at once (G15), or one unit's rate where the record's own money is the whole (a booking's Room rate beside
    // its Room charge of nights times rate, Meera Residency, 30 Oct).
    if (read === undefined || own === undefined || own.key === read.key || read.type !== 'money' || !(PAID.test(read.label) || UNIT_RATE.test(read.label))) continue;
    const plan = planFor(draft, record, rule.pattern);
    if (plan === null) continue;
    const account = rule.pattern === 'sale' ? 'income' : 'expense';
    const held = rule.roles?.[account];
    const planned = plan.rule.roles?.[account];
    const keepAccount = held !== undefined && (planned === undefined || !('fieldKey' in planned));
    const roles: Record<string, MoneyRoleSource> = { ...(rule.roles ?? {}), ...(plan.rule.roles ?? {}), ...(keepAccount ? { [account]: held } : {}) };
    const named = new Set(Object.values(roles).flatMap(rowsNamedBy));
    const rows = plan.rows.filter((row) => named.has(row.name.toLowerCase()));
    const made = moneyPlanOps(draft, record.key, { rule: { ...rule, roles }, rows });
    if (made !== null) ops.push(...made);
  }
  return ops;
};

/**
 * The ops that give a sale's or a purchase's paid part its place: a rule
 * that reads what was paid at once but not where it went counted an order
 * paid in cash on the spot as still owed (Meera Residency, 30 Oct). The place
 * reads the record's mode, each choice its account (`placeOf`); a record with
 * no mode is left, as is a rule that names its place.
 */
export const unplacedPaidOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  if (accountsListOf(draft) === null) return [];
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined || (rule.pattern !== 'sale' && rule.pattern !== 'purchase')) continue;
    const roles = rule.roles ?? {};
    if (roles.paid_now === undefined || roles.paid_to !== undefined || modeFieldOf(draft, record) === undefined) continue;
    const place = placeOf(draft, record, rule.pattern === 'sale' ? 'in' : 'out');
    const made = moneyPlanOps(draft, record.key, { rule: { ...rule, roles: { ...roles, paid_to: place.source } }, rows: place.rows });
    if (made !== null) ops.push(...made);
  }
  return ops;
};

/* ------------------------------------------------------ what was paid */

/** The parts a sale or a purchase may say were paid at once, each to its own place (M1, M2). */
const PAID_SLOTS = [
  { now: 'paid_now', to: 'paid_to', on: 'paid_on' },
  { now: 'paid_now_2', to: 'paid_to_2', on: 'paid_on_2' },
  { now: 'paid_now_3', to: 'paid_to_3', on: 'paid_on_3' },
] as const;

/** The fields a rule's roles read, its choices' too. */
const fieldsRead = (rule: MoneyRule): Set<string> => {
  const keys = new Set<string>();
  for (const roles of [rule.roles ?? {}, ...Object.values(rule.byChoice?.options ?? {}).map((one) => one.roles ?? {})]) {
    for (const source of Object.values(roles)) if ('fieldKey' in source) keys.add(source.fieldKey);
  }
  return keys;
};

/** The fields a sum (`a + b`, a first-filled `a or 0`) reads, never through a total of lines. */
const summedFields = (node: SetupDraftFormulaNode): string[] => {
  if (node.kind === 'field') return [node.fieldKey];
  if (node.kind === 'calc' && node.op === 'add') return [...summedFields(node.left), ...summedFields(node.right)];
  if (node.kind === 'coalesce') return node.of.flatMap(summedFields);
  return [];
};

/** The fields a rule's parts paid at once read, in slot order. */
const partsRead = (roles: Readonly<Record<string, MoneyRoleSource>>): string[] =>
  PAID_SLOTS.flatMap((slot) => {
    const source = roles[slot.now];
    return source !== undefined && 'fieldKey' in source ? [source.fieldKey] : [];
  });

/**
 * The parts a day's total adds when it adds only parts its rule pays at once
 * (Total = Cash total + UPI total): the day was paid in full in them, so no
 * other field is a part paid, and nothing of it is owed. Null when the
 * amount is no such sum of two or more of them.
 */
const partsOfTotal = (record: StudioRecord, roles: Readonly<Record<string, MoneyRoleSource>>): string[] | null => {
  const amount = roles.amount;
  const sum = amount !== undefined && 'fieldKey' in amount ? record.fields.find((field) => field.key === amount.fieldKey)?.formula?.expr : undefined;
  if (sum === undefined) return null;
  const summed = summedFields(sum);
  const parts = partsRead(roles);
  return summed.length >= 2 && summed.every((key) => parts.includes(key)) ? summed : null;
};

/** The fields a Balance takes from the amount: `amount - paid`, `amount - (advance + received)`. */
const takenFromAmount = (record: StudioRecord, amountKey: string): Set<string> => {
  const keys = new Set<string>();
  const walk = (node: SetupDraftFormulaNode): void => {
    if (node.kind === 'calc') {
      if (node.op === 'subtract' && summedFields(node.left).includes(amountKey)) for (const key of summedFields(node.right)) keys.add(key);
      walk(node.left);
      walk(node.right);
    } else if (node.kind === 'coalesce') node.of.forEach(walk);
    else if (node.kind === 'if') [node.left, node.then, node.otherwise, ...(node.right === undefined ? [] : [node.right])].forEach(walk);
  };
  // Only the bill's Balance says what was paid: a Trip profit (freight - diesel - toll - batta) takes costs from the
  // amount, never payments (trucks, 31 Oct 2026: Diesel and FASTag toll were counted as paid by the party).
  for (const field of record.fields) if (field.formula !== undefined && BALANCE_LABEL.test(field.label)) walk(field.formula.expr);
  for (const key of [...keys]) if (COST_LABEL.test(record.fields.find((field) => field.key === key)?.label ?? '')) keys.delete(key);
  return keys;
};

/** A field that is what is still owed on the entry. */
const BALANCE_LABEL = /\b(balance|baaki|baki|due|pending|remaining|sisa|owed|outstanding|left to pay)\b/iu;
/** A money field that is a cost of the job, never a payment: diesel, toll, batta, a commission. */
const COST_LABEL = /\b(diesel|petrol|fuel|toll|fastag|batta|expenses?|cost|kharch\w*|commission|komisen|discount|fee|charges?|rent|salary|wages?)\b/iu;

/**
 * What a bill says was paid at once that its rule does not read: a typed
 * money field named for it (Paid now, Advance, Dibayar), or on a sale one its
 * Balance takes from the amount, in the record's order. Never the amount, nor
 * a field a role reads. A purchase's Balance may take what was spent or
 * returned (a driver's Balance with driver), so only its name says paid there.
 */
export const unreadPaidFields = (record: StudioRecord, rule: MoneyRule): SetupDraftField[] => {
  const amount = rule.roles?.amount;
  const amountKey = amount !== undefined && 'fieldKey' in amount ? amount.fieldKey : amountFieldOf(record)?.key;
  // A day paid in full in its parts pays nothing more: its Bacha galla takes the galla's kharcha, never a payment (kirana, round 2).
  if (amountKey === undefined || partsOfTotal(record, rule.roles ?? {}) !== null) return [];
  const read = fieldsRead(rule);
  const taken = rule.pattern === 'sale' ? takenFromAmount(record, amountKey) : new Set<string>();
  // A "Total received" sums what came in, the advance with it: a part paid at once is never a total (trucks, 31 Oct).
  return record.fields.filter((field) => field.type === 'money' && field.key !== amountKey && !read.has(field.key) && !/\btotal\b/iu.test(field.label) && (PAID.test(field.label) || taken.has(field.key)));
};

/**
 * The ops that count what a bill says was paid at once (M2, the runs of 30
 * Oct): a yarn trader's sale whose rule a model wrote with its mode as the
 * place and nothing paid posted 258,000 with Paid now 50,000 as paid in full
 * in Cash; a lodge's Dibayar and a distributor's Paid beside Payments lines
 * were counted nowhere. Each such field (`unreadPaidFields`) is a part paid
 * now, in the first free slot (`paid_now`, then `paid_now_2`, `paid_now_3`),
 * paid into the rule's place, or the record's mode when it names none
 * (`placeOf`); a later part (Balance received after delivery) into the same
 * place. A record with no place for it, no mode and none on its rule, is left.
 */
export const paidNowOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  if (accountsListOf(draft) === null) return [];
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined || (rule.pattern !== 'sale' && rule.pattern !== 'purchase')) continue;
    const paid = unreadPaidFields(record, rule);
    if (paid.length === 0) continue;
    const roles: Record<string, MoneyRoleSource> = { ...(rule.roles ?? {}) };
    let rows: AccountRowNeed[] = [];
    if (roles.paid_to === undefined) {
      if (modeFieldOf(draft, record) === undefined) continue;
      const place = placeOf(draft, record, rule.pattern === 'sale' ? 'in' : 'out');
      roles.paid_to = place.source;
      rows = place.rows;
    }
    const free = PAID_SLOTS.filter((slot) => roles[slot.now] === undefined);
    paid.slice(0, free.length).forEach((field, at) => {
      const slot = free[at]!;
      roles[slot.now] = { fieldKey: field.key };
      if (slot.to !== 'paid_to') roles[slot.to] = roles.paid_to!;
    });
    if (roles.paid_on === undefined && rule.roles?.paid_now === undefined) {
      const day = paidOnFieldOf(record);
      if (day !== undefined && !fieldsRead(rule).has(day.key)) roles.paid_on = { fieldKey: day.key };
    }
    const made = moneyPlanOps(draft, record.key, { rule: { ...rule, roles }, rows });
    if (made !== null) ops.push(...made);
  }
  return ops;
};

const zeroNode: SetupDraftFormulaNode = { kind: 'const', value: 0 };

/**
 * The ops that make a bill's Received and Balance count what was paid at once
 * beside its payment lines (M2): the Payments kit's sums read only the lines,
 * so a lodge's Balance stayed 700,000 after a DP of 150,000. A total of the
 * lines' Paid in a worked-out field (or a must-be) of a record whose rule
 * reads both becomes what was paid at once plus that total, as a hotel's
 * Advance and Later payments add up (`library/hotel.ts`). A field that reads
 * a part paid now already is left.
 */
export const paidSumOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined) continue;
    const lines = rule.roles?.payments;
    const paidOf = lines !== undefined && 'fieldKey' in lines ? lines.each?.amount : undefined;
    if (lines === undefined || !('fieldKey' in lines) || paidOf === undefined || !('fieldKey' in paidOf)) continue;
    const now = PAID_SLOTS.flatMap((slot) => {
      const source = rule.roles?.[slot.now];
      return source !== undefined && 'fieldKey' in source ? [source.fieldKey] : [];
    });
    if (now.length === 0) continue;
    const ofLines = (node: SetupDraftFormulaNode): boolean => node.kind === 'agg' && node.lines === lines.fieldKey && node.of === paidOf.fieldKey;
    const paidNow = now.map((key): SetupDraftFormulaNode => ({ kind: 'coalesce', of: [{ kind: 'field', fieldKey: key }, zeroNode] })).reduce((left, right) => ({ kind: 'calc', op: 'add', left, right }));
    const both = (sum: SetupDraftFormulaNode): SetupDraftFormulaNode => ({ kind: 'calc', op: 'add', left: paidNow, right: sum });
    let found = false;
    const rewrite = (node: SetupDraftFormulaNode): SetupDraftFormulaNode => {
      if (node.kind === 'coalesce' && node.of[0] !== undefined && ofLines(node.of[0])) {
        found = true;
        return both(node);
      }
      if (ofLines(node)) {
        found = true;
        return both({ kind: 'coalesce', of: [node, zeroNode] });
      }
      if (node.kind === 'calc') return { ...node, left: rewrite(node.left), right: rewrite(node.right) };
      if (node.kind === 'coalesce') return { ...node, of: node.of.map(rewrite) };
      if (node.kind === 'round') return { ...node, of: rewrite(node.of) };
      if (node.kind === 'if') return { ...node, left: rewrite(node.left), ...(node.right === undefined ? {} : { right: rewrite(node.right) }), then: rewrite(node.then), otherwise: rewrite(node.otherwise) };
      return node;
    };
    const readsNow = (node: SetupDraftFormulaNode): boolean => summedFields(node).some((key) => now.includes(key));
    /** The tree with each total of the lines' Paid made the whole paid; null when it holds none, or counts a part paid now already. */
    const counted = (expr: SetupDraftFormulaNode): SetupDraftFormulaNode | null => {
      found = false;
      const out = rewrite(expr);
      if (!found) return null;
      const held = JSON.stringify(expr);
      return now.some((key) => held.includes(`"fieldKey":"${key}"`)) || readsNow(expr) ? null : out;
    };
    for (const field of record.fields) {
      if (field.formula === undefined) continue;
      const expr = counted(field.formula.expr);
      if (expr !== null) ops.push({ op: 'update_field', record: record.key, field: field.key, set: { formula: { ...field.formula, expr } } });
    }
    const rules = (record.rules ?? []).map((one) => {
      const expr = counted(one.formula.expr);
      return expr === null ? one : { ...one, formula: { ...one.formula, expr } };
    });
    if (rules.some((one, at) => one !== record.rules![at])) ops.push({ op: 'update_record', record: record.key, rules });
  }
  return ops;
};

/** The words around a mode in a money field's name: "Cash amount", "Uang QRIS", "UPI sales". */
const PART_FILLER = /\b(amount|amt|uang|duit|wang|jumlah|total|sales?|takings?|collection|collected|money|in|by|via|ka|ki|ke|se|of|the|today|day)\b/gi;

/** The mode a money field is the part of, in its own words ("UPI" of "UPI amount"), or null. */
const modePartOf = (field: SetupDraftField): string | null => {
  if (field.type !== 'money' || PAID.test(field.label) || NOT_PAID.test(field.label) || OWED_MODE.test(field.label)) return null;
  const word = fold(field.label.replace(PART_FILLER, ' '));
  return word !== '' && (CASH.test(word) || BANK_MODE.test(word) || APP_MODE.test(word)) ? word : null;
};

const wordsIn = (label: string): Set<string> => new Set(label.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((one) => one !== ''));

/**
 * A day already paid in parts, mended (kirana, round 2): a part the day's
 * total does not add is no part paid (Galle se kharcha, which Bacha galla
 * takes from the total, was a third part, so the day looked short and got a
 * payments record), and a part placed in a row named for its own field ("UPI
 * total") goes to the account its mode names, the bank. Empty when nothing
 * changes.
 */
const heldPartsOps = (record: StudioRecord, rule: MoneyRule): StudioOp[] => {
  const roles = rule.roles ?? {};
  const total = partsOfTotal(record, roles);
  const next: Record<string, MoneyRoleSource> = Object.fromEntries(Object.entries(roles).filter(([role]) => !PAID_SLOTS.some((slot) => slot.now === role || slot.to === role || slot.on === role)));
  let changed = false;
  let at = 0;
  for (const slot of PAID_SLOTS) {
    const now = roles[slot.now];
    if (now === undefined) continue;
    const field = 'fieldKey' in now ? record.fields.find((one) => one.key === now.fieldKey) : undefined;
    if (total !== null && field !== undefined && !total.includes(field.key)) {
      changed = true;
      continue;
    }
    const into = PAID_SLOTS[at++]!;
    if (into !== slot) changed = true;
    let to = roles[slot.to] ?? roles.paid_to;
    const word = field === undefined ? null : modePartOf(field);
    if (to !== undefined && 'row' in to && field !== undefined && word !== null && fold(to.row).toLowerCase() === fold(field.label).toLowerCase()) {
      to = modeAccountOf(word, 'in').source;
      changed = true;
    }
    next[into.now] = now;
    if (to !== undefined) next[into.to] = to;
    const on = roles[slot.on];
    if (on !== undefined) next[into.on] = on;
  }
  return changed ? [{ op: 'update_record', record: record.key, posting: { ...rule, roles: next } }] : [];
};

/**
 * The ops that post each mode's part of a day's total to its own account
 * (M1, the runs of 30 Oct): a kirana's Galla kept the day's Cash amount and
 * UPI amount, a lodge's cafe its Uang tunai and Uang QRIS, and each rule put
 * the whole in the one place its How paid named, so Cash held the UPI money
 * and the bank never saw it. A sale or a money in (with no party's dues to
 * clear) whose record holds two or three money fields each named for a mode
 * (`modePartOf`) becomes a sale paid at once in those parts (`paid_now`,
 * `paid_now_2`, `paid_now_3`), each to the account its mode names: the
 * option of the rule's own mode that shares its word (QRIS to the row "QR
 * (QRIS)"), else the mode's account (`modeAccountOf`). The amount is the
 * field that adds the parts, when there is one; parts that fall short of it
 * wait in To sort. The old mode then names nothing, so it is no longer asked.
 */
export const modePartsOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const list = accountsListOf(draft);
  if (list === null) return [];
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined || (rule.pattern !== 'sale' && rule.pattern !== 'money_in')) continue;
    const roles = rule.roles ?? {};
    if (rule.pattern === 'sale' && roles.paid_now_2 !== undefined) {
      ops.push(...heldPartsOps(record, rule));
      continue;
    }
    if (roles.paid_now !== undefined || roles.payments !== undefined || roles.against_dues !== undefined) continue;
    const amount = roles.amount;
    const amountKey = amount !== undefined && 'fieldKey' in amount ? amount.fieldKey : undefined;
    const parts = record.fields.flatMap((field) => {
      const word = field.key === amountKey ? null : modePartOf(field);
      return word === null ? [] : [{ field, word }];
    });
    if (parts.length < 2 || parts.length > PAID_SLOTS.length) continue;
    const income = rule.pattern === 'sale' ? roles.income : roles.account;
    if (income === undefined) continue;
    // The rule's own mode: an option that shares a part's word is where that part went.
    const mode = roles.paid_to;
    const modeField = mode !== undefined && 'fieldKey' in mode ? record.fields.find((one) => one.key === mode.fieldKey) : undefined;
    const placeOfPart = (word: string): { source: MoneyRoleSource; row: AccountRowNeed | null } => {
      const words = wordsIn(word);
      const option = (modeField?.options ?? []).find((one) => [...wordsIn(one.label)].some((held) => words.has(held)));
      const mapped = option === undefined || mode === undefined || !('fieldKey' in mode) ? undefined : mode.options?.[option.key];
      return mapped !== undefined ? { source: mapped, row: null } : modeAccountOf(word, 'in');
    };
    // The field that adds exactly the parts is the day's total.
    const keys = parts.map((one) => one.field.key).sort().join(' ');
    const sum = record.fields.find((field) => field.formula !== undefined && field.formula.result === 'money' && [...summedFields(field.formula.expr)].sort().join(' ') === keys);
    const kept = Object.fromEntries(
      Object.entries(roles).filter(([role]) => !['amount', 'income', 'account', 'paid_to', 'receivable'].includes(role) && PATTERN_ROLES.sale[role] !== undefined),
    );
    const next: Record<string, MoneyRoleSource> = { ...kept, amount: sum === undefined ? amount! : { fieldKey: sum.key }, income };
    const rows: AccountRowNeed[] = [];
    parts.forEach((part, at) => {
      const slot = PAID_SLOTS[at]!;
      const placed = placeOfPart(part.word);
      next[slot.now] = { fieldKey: part.field.key };
      next[slot.to] = placed.source;
      if (placed.row !== null) rows.push(placed.row);
    });
    if (next.amount === undefined) continue;
    const made = moneyPlanOps(draft, record.key, { rule: { pattern: 'sale', roles: next }, rows: rowsOnce(rows) });
    if (made === null) continue;
    ops.push(...made);
    if (modeField?.required === true && !Object.values(next).some((source) => 'fieldKey' in source && source.fieldKey === modeField.key)) {
      ops.push({ op: 'update_field', record: record.key, field: modeField.key, set: {}, clear: ['required'] });
    }
  }
  return ops;
};

/**
 * The ops that tax a sale or a purchase on its amount before tax, never on a
 * bill that holds the tax already (Meera Residency, 30 Oct): Bookings read a
 * Room bill of ₹7,840, the room's ₹7,000 with its 12%, at a rate on top, so it
 * counted Sales ₹7,840 and GST ₹940.80, and the guest owed ₹8,780.80. Where
 * the record holds its amount before tax ("Room amount (before GST)",
 * "Taxable value"), the rule reads that amount at the rate, and what is owed
 * is that amount with its tax. A rule that says how its tax is held
 * (`tax_basis`), or reads the bill's own tax amount, stays.
 */
export const taxedTwiceOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined || (rule.pattern !== 'sale' && rule.pattern !== 'purchase')) continue;
    const roles = rule.roles ?? {};
    if (roles.tax === undefined || roles.tax_basis !== undefined || roles.tax_amount !== undefined || roles.lines !== undefined) continue;
    const amount = roles.amount;
    if (amount === undefined || !('fieldKey' in amount)) continue;
    const taxable = record.fields.find((field) => isMoney(field) && TAXABLE.test(field.label));
    if (taxable === undefined || taxable.key === amount.fieldKey) continue;
    ops.push({ op: 'update_record', record: record.key, posting: { ...rule, roles: { ...roles, amount: { fieldKey: taxable.key } } } });
  }
  return ops;
};

/**
 * The ops that give a held sale or purchase rule the tax roles its record
 * says (G18): a rule written before its record's lines held GST %, or by a
 * hand that left the tax out, posted the tax as income (a credit sale of
 * 12,000 at 5% counted 12,600 of sales and no CGST or SGST). A rule that
 * maps any tax role already (`tax`, `lines`) is the person's and stays.
 */
export const untaxedRuleOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined || (rule.pattern !== 'sale' && rule.pattern !== 'purchase')) continue;
    const roles = rule.roles ?? {};
    if (roles.tax_amount !== undefined) continue;
    const amount = roles.amount;
    const add = taxRolesOf(record, amount !== undefined && 'fieldKey' in amount ? amount.fieldKey : undefined);
    if (Object.keys(add).length === 0) continue;
    if (add.tax_amount !== undefined) {
      // The bill's own tax wins over a rate (G18): a rate posts tax with the GST toggle off, the bill's tax never does.
      const { tax: _tax, tax_basis: _basis, lines: _lines, ...rest } = roles;
      ops.push({ op: 'update_record', record: record.key, posting: { ...rule, roles: { ...rest, ...add } } });
      continue;
    }
    if (roles.tax !== undefined || roles.lines !== undefined || roles.tax_basis !== undefined) continue;
    ops.push({ op: 'update_record', record: record.key, posting: { ...rule, roles: { ...roles, ...add } } });
  }
  return ops;
};

/** A switch that names its rate: "GST 12%", "12% GST", "VAT @ 5%". */
const RATE_IN_LABEL = /(\d+(?:\.\d+)?)\s*%/;
const NO_TAX_WORD = /\b(no|without|exempt|nil|non)\b/i;

/**
 * The ops that give a sale or purchase whose only tax is a switch naming its
 * rate (a booking's "GST 12%" yes or no) a worked-out rate its rule reads
 * (G40, Meera Residency, 30 Oct): "GST %", the named rate while the switch is
 * on and 0 while it is off. Without it the rule had no tax role and a room's
 * 12% was counted nowhere. A record that holds a rate, a tax amount or lines
 * with a rate is left to `untaxedRuleOps`, and a rule that maps any tax role
 * already stays.
 */
export const rateSwitchOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined || (rule.pattern !== 'sale' && rule.pattern !== 'purchase')) continue;
    const roles = rule.roles ?? {};
    if (roles.tax !== undefined || roles.tax_amount !== undefined || roles.lines !== undefined) continue;
    const amount = roles.amount;
    if (Object.keys(taxRolesOf(record, amount !== undefined && 'fieldKey' in amount ? amount.fieldKey : undefined)).length > 0) continue;
    const switched = record.fields.find((field) => field.type === 'boolean' && TAX_WORD.test(field.label) && RATE_IN_LABEL.test(field.label) && !NO_TAX_WORD.test(field.label));
    if (switched === undefined) continue;
    const rate = Number(RATE_IN_LABEL.exec(switched.label)![1]);
    const word = TAX_WORD.exec(switched.label)![1]!.toUpperCase();
    let key = `${word.toLowerCase()}_rate`;
    for (let n = 2; record.fields.some((field) => field.key === key); n += 1) key = `${word.toLowerCase()}_rate_${n}`;
    const worked: SetupDraftField = {
      key,
      label: `${word} %`,
      type: 'formula',
      formula: {
        result: 'number',
        expr: { kind: 'if', left: { kind: 'field', fieldKey: switched.key }, op: 'eq', right: { kind: 'const', value: true }, then: { kind: 'const', value: rate }, otherwise: { kind: 'const', value: 0 } },
        onMissing: 'zero',
      },
    } as SetupDraftField;
    ops.push({ op: 'add_field', record: record.key, field: worked });
    ops.push({ op: 'update_record', record: record.key, posting: { ...rule, roles: { ...roles, tax: { fieldKey: key } } } });
  }
  return ops;
};

/** The role a pattern reads what the money was for by. */
const FOR_ROLE_OF: Readonly<Partial<Record<PostingPattern, { readonly role: string; readonly type: 'income' | 'expense' }>>> = {
  money_in: { role: 'account', type: 'income' },
  money_out: { role: 'account', type: 'expense' },
  sale: { role: 'income', type: 'income' },
  purchase: { role: 'expense', type: 'expense' },
};

/**
 * The ops that mend a rule whose role for what the money was for (an
 * expense's `account`, a sale's `income`) reads a field named for where the
 * money is (G19): Expenses counted under "Paid from account", an optional
 * pick, refused every ₹8,000 of godown rent that left it empty, while its
 * Category (Godown rent, Transport, Salaries) said what each was for. The
 * role reads what `forOf` says now: the Category choice, each option its
 * Accounts row (added when the list lacks it), else the record's own row.
 */
export const misreadForOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  if (accountsListOf(draft) === null) return [];
  const ops: StudioOp[] = [];
  let running: Pick<StudioDraft, 'records'> = draft;
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined) continue;
    const of = FOR_ROLE_OF[rule.pattern];
    if (of === undefined) continue;
    const source = rule.roles?.[of.role];
    if (source === undefined || !('fieldKey' in source)) continue;
    const read = record.fields.find((field) => field.key === source.fieldKey);
    // Read from where the money went, or from an optional pick while the record keeps a choice of heads (G19).
    if (read === undefined || (!isPlaceLabel(read.label) && read.required === true)) continue;
    const fallback: { source: MoneyRoleSource; row: AccountRowNeed | null } =
      of.type === 'income' ? { source: { systemKey: 'sales' }, row: null } : { source: { row: fold(record.name) }, row: ownRow(record, 'expense') };
    const what = forOf(running, record, of.type, fallback.source, fallback.row);
    if ('fieldKey' in what.source && what.source.fieldKey === read.key) continue;
    // An optional pick with no choice beside it stays: `requiredRoleFieldOps` makes it asked.
    if (!isPlaceLabel(read.label) && !('fieldKey' in what.source)) continue;
    const made = moneyPlanOps(running, record.key, { rule: { ...rule, roles: { ...(rule.roles ?? {}), [of.role]: what.source } }, rows: what.rows });
    if (made === null) continue;
    ops.push(...made);
    const added = made.flatMap((op) => (op.op === 'seed_list' ? op.rows : []));
    const list = accountsListOf(running)!;
    if (added.length > 0) running = { records: running.records.map((one) => (one.key === list.key ? { ...one, seedRows: [...(one.seedRows ?? []), ...added] } : one)) };
  }
  return ops;
};

/**
 * The fields a required role reads that the form leaves optional (G19): an
 * entry that leaves one empty could never count, so the form asks for it. A
 * worked-out field, or one that starts with a value, is left as it is.
 */
export const requiredRoleFieldOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null) continue;
    const sets: Array<{ pattern: string; roles: MoneyRule['roles'] }> = [
      { pattern: rule.pattern, roles: rule.roles },
      ...Object.values(rule.byChoice?.options ?? {}).map((option) => ({ pattern: option.pattern ?? rule.pattern, roles: { ...(rule.roles ?? {}), ...(option.roles ?? {}) } })),
    ];
    const keys = new Set<string>();
    for (const one of sets) {
      if (!isPostingPattern(one.pattern)) continue;
      const defs = PATTERN_ROLES[one.pattern];
      for (const [role, source] of Object.entries(one.roles ?? {})) {
        if (defs[role]?.required !== true || !('fieldKey' in source)) continue;
        // A role a choice's other option leaves out is not needed on every entry.
        if (sets.some((other) => other.roles?.[role] === undefined)) continue;
        keys.add(source.fieldKey);
      }
    }
    for (const key of keys) {
      const field = record.fields.find((one) => one.key === key);
      if (field === undefined || field.required === true || field.type === 'formula' || field.type === 'rollup' || field.type === 'lines' || field.startsWith !== undefined) continue;
      ops.push({ op: 'update_field', record: record.key, field: key, set: { required: true } });
    }
  }
  return ops;
};

/**
 * The ops that make a sale's party the pick, never a name typed beside it
 * (Meera Residency, 30 Oct): Bookings kept a required "Guest name" and an
 * optional Guest pick its rule read as the party, so OS wrote the name, left
 * the pick empty, and the ₹5,000 still owed went to To sort. The pick is
 * required, and the name starts as the picked row's own (a text title still,
 * so other records can pick a booking by it), no longer required of itself.
 */
export const partyNameOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || (rule.pattern !== 'sale' && rule.pattern !== 'purchase')) continue;
    const party = rule.roles?.party;
    if (party === undefined || !('fieldKey' in party)) continue;
    const pick = record.fields.find((field) => field.key === party.fieldKey);
    if (pick === undefined || pick.type !== 'reference' || pick.required === true) continue;
    const list = draft.records.find((one) => one.key === pick.targetRecordKey);
    const title = list?.fields.find((field) => field.key === list.headlineFieldKey);
    if (list === undefined || title === undefined || (title.type !== 'text' && title.type !== 'phone')) continue;
    const noun = fold(list.entryNoun ?? '').trim();
    const named = record.fields.find(
      (field) => field.type === 'text' && field.required === true && field.startsWith === undefined && noun !== '' && new RegExp(`\\b${noun.replace(/[^a-z0-9 ]/g, '')}\\b`, 'i').test(field.label),
    );
    if (named === undefined) continue;
    ops.push(
      { op: 'update_field', record: record.key, field: pick.key, set: { required: true } },
      {
        op: 'update_field',
        record: record.key,
        field: named.key,
        set: { startsWith: { result: 'text', onMissing: 'blank', expr: { kind: 'hop', via: pick.key, fieldKey: title.key } } },
        clear: ['required'],
      },
    );
  }
  return ops;
};

/**
 * The ops that mend a sale others charge to, and the sales charged (G29, G31,
 * the hotel of 30 Oct):
 *
 *   its party       a sale that names no party takes its own pick of who
 *                   owes (a booking's Guest, added after its rule was drawn:
 *                   Meera Residency, 30 Oct); with none, one that another
 *                   record's sale picks as its party (a booking its orders
 *                   charge) is its own party (L46): its dues show on it or
 *                   on the guest, never in To sort
 *   its own part    its amount never holds a total over what those records
 *                   post themselves (`ownAmountOf`): Room rent, never Room
 *                   rent + Room bill
 *   charged to      a sale charged to a party with a choice of paid there and
 *                   then or put on what it owes picks its pattern by it: on
 *                   credit to the party, or paid to the mode's account; and
 *                   the party's total over them counts only what was put on
 *                   it (the Room bill), never what was paid at once
 */
export const chargedSaleOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.pattern !== 'sale' || rule.byChoice !== undefined) continue;
    const party = rule.roles?.party;
    const charged = party !== undefined && 'fieldKey' in party ? chargedToOf(draft, record) : null;
    if (charged !== null) {
      const held = rule.roles?.paid_to;
      // A place that read the charged-to choice itself says owed or paid, not where the money went: the mode is the place.
      const place = held !== undefined && 'fieldKey' in held && held.fieldKey !== charged.field.key ? held : placeOf(draft, record, 'in').source;
      ops.push({ op: 'update_record', record: record.key, posting: chargedToRule(rule, charged, place) });
      // The party's total over these entries counts what was put on it.
      const target = record.fields.find((field) => field.key === (party as { fieldKey: string }).fieldKey)?.targetRecordKey;
      const on = draft.records.find((one) => one.key === target);
      for (const field of on?.fields ?? []) {
        const rollup = field.type === 'rollup' ? field.rollup : undefined;
        if (rollup === undefined || rollup.overRecordKey !== record.key || (rollup.where ?? []).length > 0 || charged.owed.length !== 1) continue;
        ops.push({ op: 'update_field', record: on!.key, field: field.key, set: { rollup: { ...rollup, where: [{ fieldKey: charged.field.key, optionKey: charged.owed[0]! }] } } });
      }
      continue;
    }
    const roles: Record<string, MoneyRoleSource> = { ...(rule.roles ?? {}) };
    let changed = false;
    // As a rule drawn now (`moneyPlanOf`): its own pick of who owes first, then itself when others charge to it.
    const picked = roles.party === undefined ? partyFieldOf(draft, record, 'in') : undefined;
    if (picked !== undefined) {
      roles.party = { fieldKey: picked.key };
      changed = true;
    } else if (roles.party === undefined && chargedThrough(draft, record.key).length > 0) {
      roles.party = { self: true };
      changed = true;
    }
    const amount = roles.amount;
    const own = amount !== undefined && 'fieldKey' in amount ? ownAmountOf(draft, record, amount.fieldKey) : null;
    if (own !== null) {
      roles.amount = { fieldKey: own.key };
      changed = true;
    }
    if (changed) ops.push({ op: 'update_record', record: record.key, posting: { ...rule, roles } });
  }
  return ops;
};

/** The role that says where a pattern's money is: it came into it, or went out of it. */
const PLACE_ROLE_OF: Readonly<Partial<Record<PostingPattern, string>>> = {
  money_in: 'paid_to',
  money_out: 'paid_from',
  receipt: 'paid_to',
  payment: 'paid_from',
  sale: 'paid_to',
  purchase: 'paid_to',
  refund: 'paid_from',
  tax_payment: 'paid_from',
  transfer: 'to',
};

/** The patterns where a mode not paid yet means something: the party's side takes it (a sale on credit). */
const OWES_PATTERNS: ReadonlySet<PostingPattern> = new Set(['money_in', 'money_out', 'sale', 'purchase']);

/**
 * The ops that make a rule read the record's own mode: a Payments received
 * whose rule says Cash while its form asks Paid by (Cash, UPI, Bank) put
 * every UPI payment in Cash. When a money record has a mode (a choice of
 * modes, or a pick of the Accounts list named for the mode, `modeFieldOf`)
 * and its rule's place role (`paid_to`, `paid_from`, a transfer's `to`)
 * is a constant, or names nothing where the pattern needs one (a sale that
 * names none is on credit, and stays so), the role reads the mode: Cash is Cash,
 * UPI, a card, a cheque or a bank transfer the bank (or the Accounts row of
 * that name, when the list holds one), and a named bank a row of its own
 * (`modeAccountOf`); a mode not paid yet is the receivable or the payable
 * where the pattern can owe, and where it cannot (a receipt on credit) the
 * rule is left as it is. A role that reads a field is never touched, nor a
 * rule that picks its pattern by a choice.
 */
export const constantPlaceOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const list = accountsListOf(draft);
  if (list === null) return [];
  const held = accountRowNames(list);
  // The rows of their own: a row a system key names (Bank) is read by its key, so a rename keeps the rule.
  const ownRows = new Set((list.seedRows ?? []).flatMap((row) => (row.account?.systemKey === undefined ? (accountRowName(list, row)?.toLowerCase() ?? []) : [])));
  const rows: AccountRowNeed[] = [];
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined) continue;
    const role = PLACE_ROLE_OF[rule.pattern];
    if (role === undefined) continue;
    // A rule paid in parts names each part's place (`modePartsOps`): a mode beside them says nothing of where the money went.
    if (rule.roles?.paid_now_2 !== undefined) continue;
    const source = rule.roles?.[role];
    if (source !== undefined && ('fieldKey' in source || 'self' in source)) continue;
    // A sale or a purchase that names no place is on credit by its rule's own say: only a place it needs is filled.
    if (source === undefined && PATTERN_ROLES[rule.pattern][role]?.required !== true) continue;
    const mode = modeFieldOf(draft, record);
    if (mode === undefined || mode.key === record.signFieldKey) continue;
    let read: MoneyRoleSource;
    const needs: AccountRowNeed[] = [];
    if (mode.type === 'reference') {
      read = { fieldKey: mode.key };
    } else {
      const side = PATTERN_DIRECTION[rule.pattern] === -1 ? 'out' : 'in';
      const options: Record<string, MoneyRoleSource> = {};
      let owesUnheld = false;
      for (const option of mode.options ?? []) {
        const account = modeAccountOf(option.label, side);
        const owes = 'systemKey' in account.source && (account.source.systemKey === 'receivable' || account.source.systemKey === 'payable');
        if (owes && !OWES_PATTERNS.has(rule.pattern)) owesUnheld = true;
        // A generic bank word the Accounts list holds a row of ("UPI") is that row.
        const named = fold(option.label);
        const bank = 'systemKey' in account.source && account.source.systemKey === 'bank';
        // A mode of its own the list holds under a fuller name ("QRIS" as "QR (QRIS)") is that row, never a second one.
        const words = wordsIn(named);
        const fuller = account.row === null ? undefined : [...ownRows].find((held) => words.size > 0 && [...words].every((one) => wordsIn(held).has(one)));
        const fullerName = fuller === undefined ? undefined : (list.seedRows ?? []).map((row) => accountRowName(list, row)).find((name) => name?.toLowerCase() === fuller);
        options[option.key] = bank && ownRows.has(named.toLowerCase()) ? { row: named } : fullerName !== undefined && fullerName !== null ? { row: fullerName } : account.source;
        if (account.row !== null && fullerName === undefined) needs.push(account.row);
      }
      if (owesUnheld || Object.keys(options).length === 0) continue;
      read = { fieldKey: mode.key, options };
    }
    rows.push(...needs);
    ops.push({ op: 'update_record', record: record.key, posting: { ...rule, roles: { ...(rule.roles ?? {}), [role]: read } } });
  }
  const missing = rowsOnce(rows).filter((row) => !held.has(row.name.toLowerCase()));
  return missing.length === 0 ? ops : [{ op: 'seed_list', record: list.key, rows: missing.map((row) => accountSeedRow(list, row)), mode: 'append' }, ...ops];
};

/**
 * The fields an optional money role reads that the draft marks required: a
 * Paid now, a discount, a round off, a commission, a cost. An empty one
 * counts as nought (a credit sale pays nothing now), so none of them is
 * asked for: only the roles a pattern cannot post without are.
 */
export const optionalMoneyFieldOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null) continue;
    const sets: Array<{ pattern: string; roles: MoneyRule['roles'] }> = [
      { pattern: rule.pattern, roles: rule.roles },
      ...Object.values(rule.byChoice?.options ?? {}).map((option) => ({ pattern: option.pattern ?? rule.pattern, roles: { ...(rule.roles ?? {}), ...(option.roles ?? {}) } })),
    ];
    const keys = new Set<string>();
    for (const one of sets) {
      if (!isPostingPattern(one.pattern)) continue;
      const defs = PATTERN_ROLES[one.pattern];
      for (const [role, source] of Object.entries(one.roles ?? {})) {
        const def = defs[role];
        if (def === undefined || def.kind !== 'amount' || def.required || !('fieldKey' in source)) continue;
        // A role a choice's other option needs is not optional on the record.
        if (sets.some((other) => isPostingPattern(other.pattern) && PATTERN_ROLES[other.pattern][role]?.required === true && other.roles?.[role] !== undefined)) continue;
        keys.add(source.fieldKey);
      }
    }
    for (const key of keys) {
      if (record.fields.find((field) => field.key === key)?.required === true) ops.push({ op: 'update_field', record: record.key, field: key, set: {}, clear: ['required'] });
    }
  }
  return ops;
};

/* ------------------------------------------------------- outgrown modes */

/**
 * The patterns whose money goes out of the business: a mode there is where it
 * was paid from, and one not paid yet is what the business owes. `expense` is
 * an older setup's name for money out. A concession lowers what a party owes
 * (its direction is out), but no money leaves: its mode, like a receipt's, is
 * the money-in side.
 */
const OUT_PATTERNS: ReadonlySet<string> = new Set(['money_out', 'purchase', 'payment', 'refund', 'tax_payment', 'expense']);

const paysOut = (pattern: string): boolean => OUT_PATTERNS.has(pattern);

/**
 * The ops that bring each rule's mode map up to the choice it reads (L45):
 * a role that reads a select of modes maps each option to an account. When
 * the person changes the options (the hotel's Cash, Card, Bank transfer and
 * Mobile wallet replaced by the local Cash and UPI), an option the map lacks
 * is read as the account its words name (`modeAccountOf`), with any row it
 * needs added to the Accounts list, and a key that is no option any more
 * leaves the map. A mapping the option keeps is never changed. Walks the
 * rule's roles, its choices' roles and a lines role's `each`. Empty when the
 * draft holds no Accounts list, or every map is current.
 */
export const outgrownModeOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const list = accountsListOf(draft);
  if (list === null) return [];
  const ops: StudioOp[] = [];
  const held = accountRowNames(list);
  const rows: AccountRowNeed[] = [];
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null) continue;
    let changed = false;
    const mend = (source: MoneyRoleSource, fields: readonly SetupDraftField[], side: 'in' | 'out'): MoneyRoleSource => {
      if (!('fieldKey' in source)) return source;
      const field = fields.find((one) => one.key === source.fieldKey);
      if (source.each !== undefined) {
        const inner = field?.type === 'lines' ? (field.lines?.fields ?? []) : [];
        const each = Object.fromEntries(Object.entries(source.each).map(([role, one]) => [role, mend(one, inner, side)]));
        return { ...source, each };
      }
      if (source.options === undefined || field === undefined || field.type !== 'select') return source;
      const options = field.options ?? [];
      const keys = options.map((one) => one.key);
      const mapped = Object.keys(source.options);
      if (keys.length === mapped.length && keys.every((key) => source.options![key] !== undefined)) return source;
      changed = true;
      return {
        ...source,
        options: Object.fromEntries(
          options.map((option) => {
            const kept = source.options![option.key];
            if (kept !== undefined) return [option.key, kept];
            const read = modeAccountOf(option.label, side);
            if (read.row !== null) rows.push(read.row);
            return [option.key, read.source];
          }),
        ),
      };
    };
    const sideOf = (pattern: string): 'in' | 'out' => (paysOut(pattern) ? 'out' : 'in');
    const rolesOf = (roles: MoneyRule['roles'], pattern: string): MoneyRule['roles'] =>
      roles === undefined ? roles : Object.fromEntries(Object.entries(roles).map(([role, one]) => [role, mend(one, record.fields, sideOf(pattern))]));
    const roles = rolesOf(rule.roles, rule.pattern);
    const byChoice =
      rule.byChoice === undefined
        ? undefined
        : {
            ...rule.byChoice,
            options: Object.fromEntries(
              Object.entries(rule.byChoice.options).map(([key, option]) => [key, option.roles === undefined ? option : { ...option, roles: rolesOf(option.roles, option.pattern ?? rule.pattern)! }]),
            ),
          };
    if (!changed) continue;
    ops.push({ op: 'update_record', record: record.key, posting: { ...rule, ...(roles === undefined ? {} : { roles }), ...(byChoice === undefined ? {} : { byChoice }) } });
  }
  const missing = rowsOnce(rows).filter((row) => !held.has(row.name.toLowerCase()));
  return missing.length === 0 ? ops : [{ op: 'seed_list', record: list.key, rows: missing.map((row) => accountSeedRow(list, row)), mode: 'append' }, ...ops];
};

/* ------------------------------------------------------- unheld rows */

/** The roles that are a party's side of a rule: what a customer owes you, or what you owe. They name the system key, never a row of their own. */
const PARTY_SIDE: Readonly<Record<string, 'receivable' | 'payable'>> = { receivable: 'receivable', payable: 'payable', cost_payable: 'payable', commission_payable: 'payable' };
/** The roles that say where the money is: it came into them or went out of them. */
const PLACE_ROLES: ReadonlySet<string> = new Set(['paid_to', 'paid_from', 'from', 'to', 'counter', 'payments.account']);
const EXPENSE_ROLES: ReadonlySet<string> = new Set(['expense', 'cost_account', 'commission_account']);
const INCOME_ROLES: ReadonlySet<string> = new Set(['income', 'income_2', 'income_3']);

/**
 * What a `{ row }` the Accounts list does not hold becomes, by the role that
 * names it: an expense role an Expense row, an income role an Income row, a
 * place the money is a row of money you have (an overdraft one you owe), and
 * a party's side (or a place whose name says not paid yet) its system key.
 * A role with a fallback system key (round off, opening balances, a tax)
 * names that key; any other the way its pattern moves money says.
 */
export const unheldAccountOf = (pattern: PostingPattern, role: string, name: string): { readonly source: MoneyRoleSource; readonly row: AccountRowNeed | null } => {
  const side = PARTY_SIDE[role] ?? (pattern === 'refund' && role === 'account' ? 'receivable' : undefined);
  if (side !== undefined) return { source: { systemKey: side }, row: null };
  const direction = PATTERN_DIRECTION[pattern];
  if (PLACE_ROLES.has(role)) {
    const read = modeAccountOf(name, direction === -1 ? 'out' : 'in');
    if ('systemKey' in read.source && (read.source.systemKey === 'receivable' || read.source.systemKey === 'payable')) return read;
    return { source: { row: name }, row: { name, type: read.row?.type === 'liability' ? 'liability' : 'asset' } };
  }
  const typed = (type: MoneyAccountType | null): { source: MoneyRoleSource; row: AccountRowNeed } => ({ source: { row: name }, row: { name, type } });
  if (EXPENSE_ROLES.has(role)) return typed('expense');
  if (INCOME_ROLES.has(role)) return typed('income');
  // A discount given costs the business; one received is income. A charge on a bill is income on a sale and a cost on a purchase.
  if (role === 'discount_account' || role === 'charge_account') {
    const given = role === 'discount_account' ? pattern !== 'purchase' : pattern === 'purchase';
    return typed(given ? 'expense' : 'income');
  }
  if (role === 'account' && (pattern === 'money_out' || pattern === 'concession')) return typed('expense');
  if (role === 'account' && pattern === 'money_in') return typed('income');
  const fallback = PATTERN_ROLES[pattern][role]?.default;
  if (fallback !== undefined && 'system_key' in fallback) return { source: { systemKey: fallback.system_key }, row: null };
  return typed(direction === 1 ? 'income' : direction === -1 ? 'expense' : null);
};

/**
 * The ops that give every `{ row }` a rule names a row of the Accounts list
 * (step 12.1): a rule a model wrote may name an account the list does not
 * hold ("Purchases", "Room rent"), and the money kit is never refused for it.
 * Each such row joins the list, named as the rule wrote it, of the type its
 * role needs (`unheldAccountOf`); a party's side names its system key
 * instead, and the rule is rewritten to say so. A row the list holds, and
 * every name the person gave, is kept. Walks each rule's roles, its choices'
 * roles, a choice's options and a lines role's `each`, and a charge plan's
 * incomes. Empty when the draft holds no Accounts list, or every row named is
 * held.
 */
export const unheldRowOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const list = accountsListOf(draft);
  if (list === null) return [];
  const held = accountRowNames(list);
  const rows: AccountRowNeed[] = [];
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    const plan = record.chargePlan;
    for (const line of plan?.lines ?? []) {
      if (line.income !== undefined && 'row' in line.income && !held.has(line.income.row.trim().toLowerCase())) rows.push({ name: fold(line.income.row), type: 'income' });
    }
    const rule = postingOf(record);
    if (rule === null) continue;
    let changed = false;
    const mend = (pattern: PostingPattern, role: string, source: MoneyRoleSource): MoneyRoleSource => {
      if ('row' in source) {
        if (held.has(source.row.trim().toLowerCase())) return source;
        const read = unheldAccountOf(pattern, role, fold(source.row));
        if (read.row !== null) rows.push(read.row);
        if ('row' in read.source && read.source.row === source.row) return source;
        changed = true;
        return read.source;
      }
      if (!('fieldKey' in source)) return source;
      const options = source.options === undefined ? undefined : Object.fromEntries(Object.entries(source.options).map(([key, one]) => [key, mend(pattern, role, one)]));
      const each = source.each === undefined ? undefined : Object.fromEntries(Object.entries(source.each).map(([sub, one]) => [sub, mend(pattern, `${role}.${sub}`, one)]));
      return { ...source, ...(options === undefined ? {} : { options }), ...(each === undefined ? {} : { each }) };
    };
    const rolesOf = (roles: MoneyRule['roles'], pattern: string): MoneyRule['roles'] =>
      roles === undefined || !isPostingPattern(pattern) ? roles : Object.fromEntries(Object.entries(roles).map(([role, one]) => [role, mend(pattern, role, one)]));
    const roles = rolesOf(rule.roles, rule.pattern);
    const byChoice =
      rule.byChoice === undefined
        ? undefined
        : {
            ...rule.byChoice,
            options: Object.fromEntries(
              Object.entries(rule.byChoice.options).map(([key, option]) => [key, option.roles === undefined ? option : { ...option, roles: rolesOf(option.roles, option.pattern ?? rule.pattern)! }]),
            ),
          };
    if (changed) ops.push({ op: 'update_record', record: record.key, posting: { ...rule, ...(roles === undefined ? {} : { roles }), ...(byChoice === undefined ? {} : { byChoice }) } });
  }
  const missing = rowsOnce(rows).filter((row) => !held.has(row.name.toLowerCase()));
  return missing.length === 0 ? ops : [{ op: 'seed_list', record: list.key, rows: missing.map((row) => accountSeedRow(list, row)), mode: 'append' }, ...ops];
};

/* -------------------------------------------------- a mode not paid yet */

/**
 * The ops that read a mode meaning not paid yet ("Baad mein", Udhaar, Credit)
 * as what the party owes, never a row of the Accounts list (D4; dairy, 31
 * Oct: Doodh kattha's Baad mein was drawn as a row of its own, so money
 * "paid later" sat in an account nobody has). Such an option of a mode a
 * rule reads takes the receivable, or the payable for money going out; a row
 * no other rule names then leaves the list. A rule that picks its pattern by
 * a choice is left.
 */
export const notPaidModeOps = (draft: Pick<StudioDraft, 'records'>): StudioOp[] => {
  const list = accountsListOf(draft);
  if (list === null) return [];
  const ops: StudioOp[] = [];
  const freed = new Set<string>();
  for (const record of draft.records) {
    const rule = postingOf(record);
    if (rule === null || rule.byChoice !== undefined || !isPostingPattern(rule.pattern)) continue;
    const side = PATTERN_DIRECTION[rule.pattern] === -1 ? 'out' : 'in';
    const roles: Record<string, MoneyRoleSource> = { ...(rule.roles ?? {}) };
    let changed = false;
    for (const role of ['paid_to', 'paid_from']) {
      const source = roles[role];
      if (source === undefined || !('fieldKey' in source) || source.options === undefined) continue;
      const field = record.fields.find((one) => one.key === source.fieldKey);
      const options: Record<string, MoneyRoleSource> = { ...source.options };
      for (const [key, to] of Object.entries(source.options)) {
        const label = field?.options?.find((one) => one.key === key)?.label;
        if (label === undefined || !NOT_PAID.test(label) || OWED_MODE.test(label) || !('row' in to)) continue;
        options[key] = { systemKey: side === 'in' ? 'receivable' : 'payable' };
        freed.add(to.row.trim().toLowerCase());
        changed = true;
      }
      if (changed) roles[role] = { ...source, options };
    }
    if (changed) ops.push({ op: 'update_record', record: record.key, posting: { ...rule, roles } });
  }
  if (ops.length === 0) return [];
  // A freed row goes when no rule of the mended draft names it any more.
  const mended = draft.records.map((record) => {
    const op = ops.find((one) => one.op === 'update_record' && one.record === record.key);
    return op !== undefined && op.op === 'update_record' && op.posting !== undefined ? { ...record, posting: op.posting } : record;
  });
  const named = new Set(
    mended.flatMap((record) => {
      const rule = postingOf(record);
      if (rule === null) return [];
      return [rule.roles ?? {}, ...Object.values(rule.byChoice?.options ?? {}).map((one) => one.roles ?? {})].flatMap((roles) => Object.values(roles).flatMap(rowsNamedBy));
    }),
  );
  const rows = list.seedRows ?? [];
  const kept = rows.filter((row) => {
    const name = accountRowName(list, row)?.trim().toLowerCase();
    return name === undefined || !freed.has(name) || named.has(name) || row.account?.systemKey !== undefined;
  });
  return kept.length === rows.length ? ops : [...ops, { op: 'seed_list', record: list.key, rows: kept, mode: 'replace' }];
};
